var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};

// src/convert-settings.ts
import { defaultSettings } from "@usertour/constants";

// src/color.ts
import chroma from "chroma-js";
function hexToHSLString(hexColor) {
  try {
    const color = chroma(hexColor);
    const [h, s, l] = color.hsl();
    const fmt = (n) => Math.round(n * 100) / 100;
    return `${fmt(h || 0)} ${fmt(s * 100)}% ${fmt(l * 100)}%`;
  } catch (error) {
    console.warn(`Failed to convert ${hexColor} to HSL string:`, error);
    return "0 0% 0%";
  }
}
function hexToHSLAString(hexColor, opacity) {
  const hsl = hexToHSLString(hexColor);
  return `${hsl} / ${opacity}`;
}
var hexToRGBStr = (hex) => {
  try {
    const color = chroma(hex);
    const [r, g, b] = color.rgb();
    return `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`;
  } catch (error) {
    console.warn(`Failed to convert ${hex} to RGB string:`, error);
    return "0, 0, 0";
  }
};
function hexToRgb(hex) {
  if (!hex)
    return [0, 0, 0];
  if (hex.match(/^#[0-9a-f]{6}$/i)) {
    return [
      Number.parseInt(hex.substring(1, 3), 16),
      Number.parseInt(hex.substring(3, 5), 16),
      Number.parseInt(hex.substring(5, 7), 16)
    ];
  }
  if (hex.match(/^#[0-9a-f]{3}$/i)) {
    return [
      Number.parseInt(hex.substring(1, 2).repeat(2), 16),
      Number.parseInt(hex.substring(2, 3).repeat(2), 16),
      Number.parseInt(hex.substring(3, 4).repeat(2), 16)
    ];
  }
  return [0, 0, 0];
}
function rgbToHex([r, g, b]) {
  const rHex = r.toString(16).padStart(2, "0");
  const gHex = g.toString(16).padStart(2, "0");
  const bHex = b.toString(16).padStart(2, "0");
  return `#${rHex}${gHex}${bHex}`;
}
function rgbToHsl([r, g, b]) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNorm:
        h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
        break;
      case gNorm:
        h = (bNorm - rNorm) / d + 2;
        break;
      default:
        h = (rNorm - gNorm) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h, s, l];
}
function getLabLuminance([r, g, b]) {
  function linearize(channel) {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }
  const y = 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
  function yToLabL(yVal) {
    return yVal <= 216 / 24389 ? yVal * (24389 / 27) : yVal ** (1 / 3) * 116 - 16;
  }
  return Math.round(yToLabL(y) * 10) / 1e3;
}
function invertColor([r, g, b]) {
  return [Math.round(255 - r), Math.round(255 - g), Math.round(255 - b)];
}
function mixRgb(color1, color2, ratio) {
  return [
    Math.max(0, Math.min(255, Math.round(color1[0] + (color2[0] - color1[0]) * ratio))),
    Math.max(0, Math.min(255, Math.round(color1[1] + (color2[1] - color1[1]) * ratio))),
    Math.max(0, Math.min(255, Math.round(color1[2] + (color2[2] - color1[2]) * ratio)))
  ];
}
function calculateMixRatio(foreground, background) {
  const fgLuminance = getLabLuminance(foreground);
  const bgLuminance = getLabLuminance(background);
  const minRatio = 0.05;
  const baseRatio = (1 - (bgLuminance - fgLuminance)) / 2 * (0.18 - minRatio) + minRatio;
  const luminanceSimilarity = 1 - Math.abs(bgLuminance - fgLuminance);
  const adjustment = ((1 - baseRatio) * luminanceSimilarity) ** 4;
  const finalRatio = baseRatio + adjustment;
  return Math.round(finalRatio * 1e3) / 1e3;
}
function generateStateColors(backgroundColor, foregroundColor) {
  const bgRgb = hexToRgb(backgroundColor);
  const fgRgb = hexToRgb(foregroundColor);
  const bgHsl = rgbToHsl(bgRgb);
  const hoverRatio = calculateMixRatio(fgRgb, bgRgb);
  let activeRatio = hoverRatio * 2;
  let activeMixColor = fgRgb;
  const midToneThreshold = 0.2;
  if (bgHsl[2] > midToneThreshold && bgHsl[2] < 1 - midToneThreshold) {
    activeMixColor = invertColor(fgRgb);
    activeRatio = calculateMixRatio(activeMixColor, bgRgb);
  }
  const hoverRgb = mixRgb(bgRgb, fgRgb, hoverRatio);
  const activeRgb = mixRgb(bgRgb, activeMixColor, activeRatio);
  return {
    hover: rgbToHex(hoverRgb),
    active: rgbToHex(activeRgb)
  };
}

// src/convert-settings.ts
import { deepmergeCustom } from "deepmerge-ts";

// src/type-utils.ts
var nativeIsArray = Array.isArray;
var ObjProto = Object.prototype;
var objToString = ObjProto.toString;
var objHasOwn = ObjProto.hasOwnProperty;
var isArray = nativeIsArray || ((obj) => objToString.call(obj) === "[object Array]");
var isUint8Array = (x) => objToString.call(x) === "[object Uint8Array]";
var isFunction = (f) => {
  return typeof f === "function";
};
var isObject = (x) => {
  return x === Object(x) && !isArray(x);
};
var isEmptyObject = (x) => {
  if (isObject(x)) {
    for (const key in x) {
      if (objHasOwn.call(x, key)) {
        return false;
      }
    }
    return true;
  }
  return false;
};
var isUndefined = (x) => x === void 0;
var isString = (x) => {
  return objToString.call(x) === "[object String]";
};
var isEmptyString = (x) => isString(x) && x.trim().length === 0;
var isNull = (x) => {
  return x === null;
};
var isNullish = (x) => isUndefined(x) || isNull(x);
var isDate = (x) => {
  return objToString.call(x) === "[object Date]";
};
var isNumber = (x) => {
  return objToString.call(x) === "[object Number]";
};
var isBoolean = (x) => {
  return objToString.call(x) === "[object Boolean]";
};
var isDocument = (x) => {
  return x instanceof Document;
};
var isFormData = (x) => {
  return x instanceof FormData;
};
var isFile = (x) => {
  return x instanceof File;
};
var toNumericValue = (value) => {
  if (value === void 0) {
    return void 0;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? void 0 : value;
  }
  if (typeof value === "string") {
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? void 0 : numericValue;
  }
  return void 0;
};

// src/utils.ts
var deepClone = (obj) => {
  try {
    return structuredClone(obj);
  } catch (error) {
    console.warn("structuredClone failed, falling back to JSON method:", error);
    return JSON.parse(JSON.stringify(obj));
  }
};
var parseUrlParams = (url, paramName) => {
  if (!url || !paramName) {
    return null;
  }
  try {
    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.search);
    if (searchParams.has(paramName)) {
      return searchParams.get(paramName);
    }
    if (urlObj.hash) {
      const hashSearch = urlObj.hash.split("?")[1];
      if (hashSearch) {
        const hashParams = new URLSearchParams(hashSearch);
        if (hashParams.has(paramName)) {
          return hashParams.get(paramName);
        }
      }
    }
    return null;
  } catch (error) {
    console.error("Error parsing URL:", error);
    return null;
  }
};
var wait = (seconds) => {
  if (typeof seconds !== "number" || Number.isNaN(seconds)) {
    return Promise.reject(new Error("Invalid wait time: must be a number"));
  }
  if (seconds < 0) {
    return Promise.reject(new Error("Invalid wait time: cannot be negative"));
  }
  if (seconds === 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    try {
      setTimeout(resolve, seconds * 1e3);
    } catch (error) {
      reject(error);
    }
  });
};

// src/convert-settings.ts
var defaultFontFamily = '-apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';
var shouldLoadGoogleFont = (fontFamily) => {
  if (!fontFamily) {
    return false;
  }
  return fontFamily !== "System font" && fontFamily !== "Custom font";
};
var buildGoogleFontUrl = (fontFamily) => {
  const encoded = fontFamily.split("|").map((font) => {
    const withSpaces = font.trim().replace(/\s+/g, "+");
    return encodeURIComponent(withSpaces).replace(/%2B/g, "+");
  }).join("%7C");
  return `https://fonts.googleapis.com/css2?family=${encoded}`;
};
var mergeWithDefaults = deepmergeCustom({
  mergeOthers: (values) => {
    const settingsValue = values[values.length - 1];
    if (settingsValue === null || settingsValue === void 0) {
      return values[0];
    }
    return settingsValue;
  }
});
var resolveAutoValue = (value, fallback) => {
  if (value === "Auto" || value === void 0 || value === null) {
    return fallback;
  }
  return value;
};
var mergeThemeDefaultSettings = (settings) => {
  const base = deepClone(defaultSettings);
  return mergeWithDefaults(base, settings);
};
var deepMergeThemeSettings = (base, patch) => mergeWithDefaults(deepClone(base), patch);
var convertSettings = (settings) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
  const data = mergeThemeDefaultSettings(settings);
  data.mainColor.hover = resolveAutoValue(data.mainColor.hover, data.mainColor.autoHover);
  data.mainColor.active = resolveAutoValue(
    data.mainColor.active,
    data.mainColor.autoActive
  );
  data.brandColor.hover = resolveAutoValue(
    data.brandColor.hover,
    data.brandColor.autoHover
  );
  data.brandColor.active = resolveAutoValue(
    data.brandColor.active,
    data.brandColor.autoActive
  );
  data.border.borderColor = resolveAutoValue(data.border.borderColor, data.mainColor.color);
  const primaryTextColorBaseAuto = data.buttons.primary.textColor.color === "Auto";
  data.buttons.primary.textColor.color = resolveAutoValue(
    data.buttons.primary.textColor.color,
    data.brandColor.color
  );
  const primaryTextColorResolved = data.buttons.primary.textColor.color;
  data.buttons.primary.textColor.hover = resolveAutoValue(
    data.buttons.primary.textColor.hover,
    primaryTextColorBaseAuto ? primaryTextColorResolved : (_a = data.buttons.primary.textColor.autoHover) != null ? _a : data.brandColor.color
  );
  data.buttons.primary.textColor.active = resolveAutoValue(
    data.buttons.primary.textColor.active,
    primaryTextColorBaseAuto ? primaryTextColorResolved : (_b = data.buttons.primary.textColor.autoActive) != null ? _b : data.brandColor.color
  );
  const primaryBgBaseAuto = data.buttons.primary.backgroundColor.background === "Auto";
  data.buttons.primary.backgroundColor.background = resolveAutoValue(
    data.buttons.primary.backgroundColor.background,
    data.brandColor.background
  );
  const primaryBgResolved = data.buttons.primary.backgroundColor.background;
  const primaryBgStates = generateStateColors(primaryBgResolved, data.brandColor.color);
  data.buttons.primary.backgroundColor.hover = resolveAutoValue(
    data.buttons.primary.backgroundColor.hover,
    primaryBgBaseAuto ? primaryBgStates.hover : (_c = data.buttons.primary.backgroundColor.autoHover) != null ? _c : data.brandColor.autoHover
  );
  data.buttons.primary.backgroundColor.active = resolveAutoValue(
    data.buttons.primary.backgroundColor.active,
    primaryBgBaseAuto ? primaryBgStates.active : (_d = data.buttons.primary.backgroundColor.autoActive) != null ? _d : data.brandColor.autoActive
  );
  const primaryBorderBaseAuto = data.buttons.primary.border.color.color === "Auto";
  data.buttons.primary.border.color.color = resolveAutoValue(
    data.buttons.primary.border.color.color,
    data.brandColor.background
  );
  const primaryBorderResolved = data.buttons.primary.border.color.color;
  const primaryBorderStates = generateStateColors(primaryBorderResolved, data.brandColor.color);
  data.buttons.primary.border.color.hover = resolveAutoValue(
    data.buttons.primary.border.color.hover,
    primaryBorderBaseAuto ? primaryBorderStates.hover : (_e = data.buttons.primary.border.color.autoHover) != null ? _e : data.brandColor.autoHover
  );
  data.buttons.primary.border.color.active = resolveAutoValue(
    data.buttons.primary.border.color.active,
    primaryBorderBaseAuto ? primaryBorderStates.active : (_f = data.buttons.primary.border.color.autoActive) != null ? _f : data.brandColor.autoActive
  );
  const secondaryTextColorBaseAuto = data.buttons.secondary.textColor.color === "Auto";
  data.buttons.secondary.textColor.color = resolveAutoValue(
    data.buttons.secondary.textColor.color,
    data.brandColor.background
  );
  const secondaryTextColorResolved = data.buttons.secondary.textColor.color;
  data.buttons.secondary.textColor.hover = resolveAutoValue(
    data.buttons.secondary.textColor.hover,
    secondaryTextColorBaseAuto ? secondaryTextColorResolved : (_g = data.buttons.secondary.textColor.autoHover) != null ? _g : data.brandColor.background
  );
  data.buttons.secondary.textColor.active = resolveAutoValue(
    data.buttons.secondary.textColor.active,
    secondaryTextColorBaseAuto ? secondaryTextColorResolved : (_h = data.buttons.secondary.textColor.autoActive) != null ? _h : data.brandColor.background
  );
  const secondaryBgBaseAuto = data.buttons.secondary.backgroundColor.background === "Auto";
  data.buttons.secondary.backgroundColor.background = resolveAutoValue(
    data.buttons.secondary.backgroundColor.background,
    data.mainColor.background
  );
  const secondaryBgResolved = data.buttons.secondary.backgroundColor.background;
  const secondaryBgStates = generateStateColors(secondaryBgResolved, data.brandColor.background);
  data.buttons.secondary.backgroundColor.hover = resolveAutoValue(
    data.buttons.secondary.backgroundColor.hover,
    secondaryBgBaseAuto ? secondaryBgStates.hover : (_i = data.buttons.secondary.backgroundColor.autoHover) != null ? _i : data.mainColor.autoHover
  );
  data.buttons.secondary.backgroundColor.active = resolveAutoValue(
    data.buttons.secondary.backgroundColor.active,
    secondaryBgBaseAuto ? secondaryBgStates.active : (_j = data.buttons.secondary.backgroundColor.autoActive) != null ? _j : data.mainColor.autoActive
  );
  const secondaryBorderBaseAuto = data.buttons.secondary.border.color.color === "Auto";
  data.buttons.secondary.border.color.color = resolveAutoValue(
    data.buttons.secondary.border.color.color,
    data.brandColor.background
  );
  const secondaryBorderResolved = data.buttons.secondary.border.color.color;
  data.buttons.secondary.border.color.hover = resolveAutoValue(
    data.buttons.secondary.border.color.hover,
    secondaryBorderBaseAuto ? secondaryBorderResolved : (_k = data.buttons.secondary.border.color.autoHover) != null ? _k : data.brandColor.background
  );
  data.buttons.secondary.border.color.active = resolveAutoValue(
    data.buttons.secondary.border.color.active,
    secondaryBorderBaseAuto ? secondaryBorderResolved : (_l = data.buttons.secondary.border.color.autoActive) != null ? _l : data.brandColor.background
  );
  data.font.linkColor = resolveAutoValue(data.font.linkColor, data.brandColor.background);
  data.xbutton.color = resolveAutoValue(data.xbutton.color, data.mainColor.color);
  data.progress.color = resolveAutoValue(data.progress.color, data.brandColor.background);
  data.launcherBeacon.color = resolveAutoValue(
    data.launcherBeacon.color,
    data.brandColor.background
  );
  data.launcherIcon.color.color = resolveAutoValue(
    data.launcherIcon.color.color,
    data.brandColor.background
  );
  data.launcherIcon.color.hover = resolveAutoValue(
    data.launcherIcon.color.hover,
    data.brandColor.hover
  );
  data.launcherIcon.color.active = resolveAutoValue(
    data.launcherIcon.color.active,
    data.brandColor.active
  );
  data.checklistLauncher.color.color = resolveAutoValue(
    data.checklistLauncher.color.color,
    data.brandColor.color
  );
  data.checklistLauncher.color.background = resolveAutoValue(
    data.checklistLauncher.color.background,
    data.brandColor.background
  );
  data.checklistLauncher.color.hover = resolveAutoValue(
    data.checklistLauncher.color.hover,
    data.brandColor.hover
  );
  data.checklistLauncher.color.active = resolveAutoValue(
    data.checklistLauncher.color.active,
    data.brandColor.active
  );
  data.checklistLauncher.counter.color = resolveAutoValue(
    data.checklistLauncher.counter.color,
    data.brandColor.background
  );
  data.checklistLauncher.counter.background = resolveAutoValue(
    data.checklistLauncher.counter.background,
    data.brandColor.color
  );
  const rcPrimary = data.brandColor.background;
  const rcHeaderForeground = data.brandColor.color;
  const rcLauncherColor = data.resourceCenterLauncherButton.color;
  rcLauncherColor.background = resolveAutoValue(rcLauncherColor.background, rcPrimary);
  rcLauncherColor.foreground = resolveAutoValue(rcLauncherColor.foreground, rcHeaderForeground);
  const rcLauncherStates = generateStateColors(
    rcLauncherColor.background,
    rcLauncherColor.foreground
  );
  rcLauncherColor.hover = resolveAutoValue(rcLauncherColor.hover, rcLauncherStates.hover);
  rcLauncherColor.active = resolveAutoValue(rcLauncherColor.active, rcLauncherStates.active);
  data.banner.textColor.color = resolveAutoValue(
    data.banner.textColor.color,
    data.brandColor.color
  );
  data.banner.backgroundColor.background = resolveAutoValue(
    data.banner.backgroundColor.background,
    data.brandColor.background
  );
  const bannerBackground = data.banner.backgroundColor.background;
  const bannerForeground = data.banner.textColor.color;
  if (bannerBackground != null && bannerForeground != null) {
    const bgStates = generateStateColors(bannerBackground, bannerForeground);
    const fgStates = generateStateColors(bannerForeground, bannerBackground);
    data.banner.backgroundColor.hover = bgStates.hover;
    data.banner.backgroundColor.active = bgStates.active;
    data.banner.textColor.hover = fgStates.hover;
    data.banner.textColor.active = fgStates.active;
  }
  data.survey.color = resolveAutoValue(data.survey.color, data.brandColor.background);
  data.checklist.checkmarkColor = resolveAutoValue(
    data.checklist.checkmarkColor,
    data.brandColor.background
  );
  const headerBackground = data.resourceCenter.headerBackground;
  headerBackground.color = resolveAutoValue(headerBackground.color, data.mainColor.active);
  headerBackground.gradientFrom = resolveAutoValue(
    headerBackground.gradientFrom,
    data.brandColor.background
  );
  headerBackground.gradientTo = resolveAutoValue(
    headerBackground.gradientTo,
    data.mainColor.background
  );
  data.focusHighlight.color = resolveAutoValue(data.focusHighlight.color, data.mainColor.active);
  data.launcherButtons.primary.textColor.color = resolveAutoValue(
    data.launcherButtons.primary.textColor.color,
    data.brandColor.color
  );
  data.launcherButtons.primary.textColor.hover = resolveAutoValue(
    data.launcherButtons.primary.textColor.hover,
    data.brandColor.color
  );
  data.launcherButtons.primary.textColor.active = resolveAutoValue(
    data.launcherButtons.primary.textColor.active,
    data.brandColor.color
  );
  data.launcherButtons.primary.backgroundColor.background = resolveAutoValue(
    data.launcherButtons.primary.backgroundColor.background,
    data.brandColor.background
  );
  data.launcherButtons.primary.backgroundColor.hover = resolveAutoValue(
    data.launcherButtons.primary.backgroundColor.hover,
    data.brandColor.hover
  );
  data.launcherButtons.primary.backgroundColor.active = resolveAutoValue(
    data.launcherButtons.primary.backgroundColor.active,
    data.brandColor.active
  );
  data.launcherButtons.primary.border.color.color = resolveAutoValue(
    data.launcherButtons.primary.border.color.color,
    data.brandColor.background
  );
  data.launcherButtons.primary.border.color.hover = resolveAutoValue(
    data.launcherButtons.primary.border.color.hover,
    data.brandColor.hover
  );
  data.launcherButtons.primary.border.color.active = resolveAutoValue(
    data.launcherButtons.primary.border.color.active,
    data.brandColor.active
  );
  if (data.font.fontFamily === "System font") {
    data.font.fontFamily = defaultFontFamily;
  } else if (data.font.fontFamily === "Custom font") {
    const customFontFamily = (_m = data.font.customFontFamily) == null ? void 0 : _m.trim();
    if (customFontFamily) {
      data.font.fontFamily = customFontFamily.includes("sans-serif") ? customFontFamily : `${customFontFamily}, sans-serif`;
    } else {
      data.font.fontFamily = defaultFontFamily;
    }
  } else if (!data.font.fontFamily.includes("sans-serif")) {
    data.font.fontFamily += ", sans-serif";
  }
  return data;
};
var convertToCssVars = (settings, type = "tooltip") => {
  var _a, _b, _c;
  const resourceCenter = (_a = settings.resourceCenter) != null ? _a : defaultSettings.resourceCenter;
  const resourceCenterLauncherButton = (_b = settings.resourceCenterLauncherButton) != null ? _b : defaultSettings.resourceCenterLauncherButton;
  const resourceCenterUnreadBadge = (_c = settings.resourceCenterUnreadBadge) != null ? _c : defaultSettings.resourceCenterUnreadBadge;
  const cssMapping = {
    "--usertour-background": hexToHSLString(settings.mainColor.background),
    "--usertour-foreground": hexToHSLString(settings.mainColor.color),
    "--usertour-brand-background-color": hexToHSLString(settings.brandColor.background),
    "--usertour-brand-foreground-color": hexToHSLString(settings.brandColor.color),
    "--usertour-brand-active-background-color": hexToHSLString(settings.brandColor.active),
    "--usertour-brand-hover-background-color": hexToHSLString(settings.brandColor.hover),
    "--usertour-font-family": settings.font.fontFamily,
    "--usertour-font-size": `${settings.font.fontSize}px`,
    "--usertour-main-hover-background-color": hexToHSLString(settings.mainColor.hover),
    "--usertour-main-active-background-color": hexToHSLString(settings.mainColor.active),
    "--usertour-line-height": `${settings.font.lineHeight}px`,
    "--usertour-widget-popper-border-radius": `${settings.border.borderRadius}px`,
    "--usertour-font-weight-normal": settings.font.fontWeightNormal,
    "--usertour-font-weight-bold": settings.font.fontWeightBold,
    "--usertour-h1-font-size": `${settings.font.h1FontSize}px`,
    "--usertour-h2-font-size": `${settings.font.h2FontSize}px`,
    "--usertour-link-color": hexToHSLString(settings.font.linkColor),
    "--usertour-widget-popper-border-width": settings.border.borderWidthEnabled ? `${settings.border.borderWidth}px` : "0px",
    "--usertour-widget-popper-border-color": settings.border.borderColor,
    "--usertour-button-border-radius": `${settings.buttons.borderRadius}px`,
    "--usertour-button-height": `${settings.buttons.height}px`,
    "--usertour-button-min-width": `${settings.buttons.minWidth}px`,
    "--usertour-button-px": `${settings.buttons.px}px`,
    "--usertour-primary": settings.buttons.primary.backgroundColor.background,
    "--usertour-primary-hover": settings.buttons.primary.backgroundColor.hover,
    "--usertour-primary-active": settings.buttons.primary.backgroundColor.active,
    "--usertour-primary-foreground": settings.buttons.primary.textColor.color,
    "--usertour-primary-foreground-hover": settings.buttons.primary.textColor.hover,
    "--usertour-primary-foreground-active": settings.buttons.primary.textColor.active,
    "--usertour-primary-border-width": settings.buttons.primary.border.enabled ? `${settings.buttons.primary.border.borderWidth}px` : "0px",
    "--usertour-primary-border-color": settings.buttons.primary.border.color.color,
    "--usertour-primary-border-hover": settings.buttons.primary.border.color.hover,
    "--usertour-primary-border-active": settings.buttons.primary.border.color.active,
    "--usertour-primary-font-weight": settings.buttons.primary.fontWeight,
    "--usertour-secondary": settings.buttons.secondary.backgroundColor.background,
    "--usertour-secondary-hover": settings.buttons.secondary.backgroundColor.hover,
    "--usertour-secondary-active": settings.buttons.secondary.backgroundColor.active,
    "--usertour-secondary-foreground": settings.buttons.secondary.textColor.color,
    "--usertour-secondary-foreground-hover": settings.buttons.secondary.textColor.hover,
    "--usertour-secondary-foreground-active": settings.buttons.secondary.textColor.active,
    "--usertour-secondary-border-width": settings.buttons.secondary.border.enabled ? `${settings.buttons.secondary.border.borderWidth}px` : "0px",
    "--usertour-secondary-border-color": settings.buttons.secondary.border.color.color,
    "--usertour-secondary-border-hover": settings.buttons.secondary.border.color.hover,
    "--usertour-secondary-border-active": settings.buttons.secondary.border.color.active,
    "--usertour-secondary-font-weight": settings.buttons.secondary.fontWeight,
    "--usertour-backdrop-color-rgb": hexToRGBStr(settings.backdrop.color),
    "--usertour-backdrop-highlight-color-rgb": hexToRGBStr(settings.backdrop.highlight.color),
    "--usertour-backdrop-highlight-opacity": settings.backdrop.highlight.opacity / 100,
    "--usertour-backdrop-highlight-radius": `${settings.backdrop.highlight.radius}px`,
    "--usertour-backdrop-highlight-spread": `${settings.backdrop.highlight.spread}px`,
    "--usertour-backdrop-opacity": settings.backdrop.opacity / 100,
    "--usertour-focus-color": hexToHSLAString(
      settings.focusHighlight.color,
      settings.focusHighlight.opacity / 100
    ),
    "--usertour-progress-bar-color": settings.progress.color,
    "--usertour-progress-bar-height": `${settings.progress.height}px`,
    "--usertour-narrow-progress-bar-height": `${settings.progress.narrowHeight}px`,
    "--usertour-squared-progress-bar-height": `${settings.progress.chainSquaredHeight}px`,
    "--usertour-rounded-progress-bar-height": `${settings.progress.chainRoundedHeight}px`,
    "--usertour-dotted-progress-bar-height": `${settings.progress.dotsHeight}px`,
    "--usertour-numbered-progress-bar-height": `${settings.progress.numberedHeight}px`,
    "--usertour-widget-popper-padding": type === "modal" ? `${settings.modal.padding}px` : void 0,
    "--usertour-xbutton": settings.xbutton.color,
    "--usertour-widget-launcher-icon-color": settings.launcherIcon.color.color,
    "--usertour-widget-launcher-icon-hover-color": settings.launcherIcon.color.hover,
    "--usertour-widget-launcher-icon-active-color": settings.launcherIcon.color.active,
    "--usertour-widget-launcher-icon-size": `${settings.launcherIcon.size}px`,
    "--usertour-widget-beacon-color": settings.launcherBeacon.color,
    "--usertour-widget-beacon-size": `${settings.launcherBeacon.size}px`,
    "--usertour-widget-launcher-icon-opacity": settings.launcherIcon.opacity / 100,
    "--usertour-widget-popper-padding-top": "2px",
    "--usertour-widget-popper-padding-bottom": "2px",
    "--usertour-checklist-trigger-active-background-color": settings.checklistLauncher.color.active,
    "--usertour-checklist-trigger-background-color": settings.checklistLauncher.color.background,
    "--usertour-checklist-trigger-border-radius": `${settings.checklistLauncher.borderRadius}px`,
    "--usertour-checklist-trigger-counter-background-color": settings.checklistLauncher.counter.background,
    "--usertour-checklist-trigger-counter-font-color": settings.checklistLauncher.counter.color,
    "--usertour-checklist-trigger-font-color-rgb": settings.checklistLauncher.color.color,
    "--usertour-checklist-trigger-font-color": hexToHSLString(
      settings.checklistLauncher.color.color
    ),
    "--usertour-checklist-trigger-font-weight": settings.checklistLauncher.fontWeight,
    "--usertour-checkmark-background-color": settings.checklist.checkmarkColor,
    "--usertour-resource-center-launcher-background-color": hexToHSLString(
      resourceCenterLauncherButton.color.background
    ),
    "--usertour-resource-center-launcher-hover-background-color": hexToHSLString(
      resourceCenterLauncherButton.color.hover
    ),
    "--usertour-resource-center-launcher-active-background-color": hexToHSLString(
      resourceCenterLauncherButton.color.active
    ),
    "--usertour-resource-center-launcher-foreground-color": hexToHSLString(
      resourceCenterLauncherButton.color.foreground
    ),
    // RC header background — 'Auto' falls back to mainColor.active so the soft
    // brand-derived tint reads well in both light and dark themes. Settings
    // here have already been Auto-resolved by convertSettings, so
    // mainColor.active is guaranteed to be a hex.
    "--usertour-resource-center-header-background-color": hexToHSLString(
      resourceCenter.headerBackground.color === "Auto" ? settings.mainColor.active : resourceCenter.headerBackground.color
    ),
    "--usertour-resource-center-launcher-border-radius": resourceCenterLauncherButton.borderRadius == null ? "calc(var(--usertour-resource-center-launcher-height) / 2)" : `${resourceCenterLauncherButton.borderRadius}px`,
    "--usertour-resource-center-launcher-font-weight": settings.font.fontWeightBold,
    "--usertour-resource-center-launcher-height": `${resourceCenterLauncherButton.height}px`,
    "--usertour-resource-center-launcher-icon-size": `${resourceCenterLauncherButton.imageHeight}px`,
    "--usertour-resource-center-badge-background-color": resourceCenterUnreadBadge.backgroundColor,
    "--usertour-resource-center-badge-background-color-rgb": hexToRGBStr(
      resourceCenterUnreadBadge.backgroundColor
    ),
    "--usertour-resource-center-badge-foreground-color": resourceCenterUnreadBadge.textColor,
    "--usertour-resource-center-width": `${resourceCenter.normalWidth}px`,
    "--usertour-resource-center-large-width": `${resourceCenter.largeWidth}px`,
    "--usertour-resource-center-max-height": resourceCenter.maxHeight ? `${resourceCenter.maxHeight}px` : "none",
    "--usertour-resource-center-transition-duration": `${resourceCenter.transitionDuration}ms`,
    "--usertour-banner-foreground-color": hexToHSLString(settings.banner.textColor.color),
    "--usertour-banner-background-color": hexToHSLString(
      settings.banner.backgroundColor.background
    ),
    "--usertour-banner-hover-background-color": hexToHSLString(
      settings.banner.backgroundColor.hover
    ),
    "--usertour-banner-active-background-color": hexToHSLString(
      settings.banner.backgroundColor.active
    ),
    "--usertour-banner-hover-foreground-color": hexToHSLString(settings.banner.textColor.hover),
    "--usertour-banner-active-foreground-color": hexToHSLString(settings.banner.textColor.active),
    "--usertour-banner-padding": `${settings.banner.padding}px`,
    "--usertour-checklist-trigger-height": `${settings.checklistLauncher.height}px`,
    "--usertour-checklist-trigger-hover-background-color": settings.checklistLauncher.color.hover,
    "--usertour-question-color": hexToHSLString(settings.survey.color),
    "--usertour-launcher-button-height": `${settings.launcherButtons.height}px`,
    "--usertour-launcher-button-width": !settings.launcherButtons.width ? "auto" : `${settings.launcherButtons.width}px`,
    "--usertour-launcher-button-horizontal-padding": `${settings.launcherButtons.px}px`,
    "--usertour-launcher-button-border-radius": `${settings.launcherButtons.borderRadius}px`,
    "--usertour-launcher-button-background-color": settings.launcherButtons.primary.backgroundColor.background,
    "--usertour-launcher-button-hover-background-color": settings.launcherButtons.primary.backgroundColor.hover,
    "--usertour-launcher-button-active-background-color": settings.launcherButtons.primary.backgroundColor.active,
    "--usertour-launcher-button-font-color": settings.launcherButtons.primary.textColor.color,
    "--usertour-launcher-button-hover-font-color": settings.launcherButtons.primary.textColor.hover,
    "--usertour-launcher-button-active-font-color": settings.launcherButtons.primary.textColor.active,
    "--usertour-launcher-button-font-weight": settings.launcherButtons.primary.fontWeight,
    "--usertour-launcher-button-border-width": settings.launcherButtons.primary.border.enabled ? `${settings.launcherButtons.primary.border.borderWidth}px` : "0px",
    "--usertour-launcher-button-border-color": settings.launcherButtons.primary.border.color.color,
    "--usertour-launcher-button-hover-border-color": settings.launcherButtons.primary.border.color.hover,
    "--usertour-launcher-button-active-border-color": settings.launcherButtons.primary.border.color.active
  };
  if (settings.backdrop.highlight.type === "inside") {
    cssMapping["--usertour-backdrop-highlight-inset"] = "inset";
  }
  let css = "";
  for (const key in cssMapping) {
    const value = cssMapping[key];
    if (!isUndefined(value)) {
      css += `${key}:${value};`;
    }
  }
  return css;
};

// src/derive-theme.ts
var resolveAuto = (value, fallback) => value === "Auto" ? fallback : value;
var deriveThemeAutoColors = (settings) => {
  const s = deepClone(settings);
  const brand = generateStateColors(s.brandColor.background, s.brandColor.color);
  s.brandColor.autoHover = brand.hover;
  s.brandColor.autoActive = brand.active;
  const main = generateStateColors(s.mainColor.background, s.brandColor.background);
  s.mainColor.autoHover = main.hover;
  s.mainColor.autoActive = main.active;
  const pText = resolveAuto(s.buttons.primary.textColor.color, s.brandColor.color);
  s.buttons.primary.textColor.autoHover = pText;
  s.buttons.primary.textColor.autoActive = pText;
  const pBg = generateStateColors(
    resolveAuto(s.buttons.primary.backgroundColor.background, s.brandColor.background),
    s.brandColor.color
  );
  s.buttons.primary.backgroundColor.autoHover = pBg.hover;
  s.buttons.primary.backgroundColor.autoActive = pBg.active;
  const pBorder = generateStateColors(
    resolveAuto(s.buttons.primary.border.color.color, s.brandColor.background),
    s.brandColor.color
  );
  s.buttons.primary.border.color.autoHover = pBorder.hover;
  s.buttons.primary.border.color.autoActive = pBorder.active;
  const sText = resolveAuto(s.buttons.secondary.textColor.color, s.brandColor.background);
  s.buttons.secondary.textColor.autoHover = sText;
  s.buttons.secondary.textColor.autoActive = sText;
  const sBg = generateStateColors(
    resolveAuto(s.buttons.secondary.backgroundColor.background, s.mainColor.background),
    s.brandColor.background
  );
  s.buttons.secondary.backgroundColor.autoHover = sBg.hover;
  s.buttons.secondary.backgroundColor.autoActive = sBg.active;
  const sBorder = resolveAuto(s.buttons.secondary.border.color.color, s.brandColor.background);
  s.buttons.secondary.border.color.autoHover = sBorder;
  s.buttons.secondary.border.color.autoActive = sBorder;
  return s;
};

// src/error.ts
import {
  AttributeBizTypes,
  AttributeDataType,
  EventCountLogic,
  EventTimeLogic
} from "@usertour/types";

// src/conditions/time.ts
import { isAfter, isBefore, isValid, parseISO } from "date-fns";
function isEmpty(value) {
  return isNullish(value) || isEmptyString(value);
}
function hasRequiredFields(data, type) {
  if (type === "start") {
    return !isEmpty(data.startDate) && !isEmpty(data.startDateHour) && !isEmpty(data.startDateMinute);
  }
  return !isEmpty(data.endDate) && !isEmpty(data.endDateHour) && !isEmpty(data.endDateMinute);
}
function parseLegacyDate(dateStr) {
  const [month, day, year] = dateStr.split("/");
  if (!month || !day || !year) {
    return null;
  }
  return {
    month: Number.parseInt(month),
    day: Number.parseInt(day),
    year: Number.parseInt(year)
  };
}
function createLegacyDateTime(data, type) {
  const dateStr = type === "start" ? data.startDate : data.endDate;
  const hourStr = type === "start" ? data.startDateHour : data.endDateHour;
  const minuteStr = type === "start" ? data.startDateMinute : data.endDateMinute;
  if (!dateStr || !hourStr || !minuteStr) {
    return null;
  }
  const dateParts = parseLegacyDate(dateStr);
  if (!dateParts) {
    return null;
  }
  const dateTime = new Date(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    Number.parseInt(hourStr),
    Number.parseInt(minuteStr),
    0
  );
  return isValid(dateTime) ? dateTime : null;
}
function isTimeConditionDataV2(data) {
  return data && ("startTime" in data || "endTime" in data);
}
function isTimeConditionDataLegacy(data) {
  return data && ("startDate" in data || "startDateHour" in data);
}
function convertTimeConditionLegacyToV2(legacyData) {
  try {
    if (!hasRequiredFields(legacyData, "start")) {
      return null;
    }
    const startDateTime = createLegacyDateTime(legacyData, "start");
    if (!startDateTime) {
      return null;
    }
    const result = {
      startTime: startDateTime.toISOString()
    };
    if (hasRequiredFields(legacyData, "end")) {
      const endDateTime = createLegacyDateTime(legacyData, "end");
      if (endDateTime) {
        result.endTime = endDateTime.toISOString();
      }
    }
    return result;
  } catch {
    return null;
  }
}
function normalizeTimeConditionData(data) {
  if (!data) {
    return null;
  }
  if (isTimeConditionDataV2(data)) {
    return data;
  }
  if (isTimeConditionDataLegacy(data)) {
    return convertTimeConditionLegacyToV2(data);
  }
  return null;
}
function evaluateTimeConditionV2(data) {
  if (!data.startTime) {
    return false;
  }
  const startTime = parseISO(data.startTime);
  if (!isValid(startTime)) {
    return false;
  }
  const now = /* @__PURE__ */ new Date();
  if (!data.endTime) {
    return isAfter(now, startTime);
  }
  const endTime = parseISO(data.endTime);
  if (!isValid(endTime)) {
    return false;
  }
  return isAfter(now, startTime) && isBefore(now, endTime);
}
function evaluateTimeConditionLegacy(data) {
  if (!hasRequiredFields(data, "start")) {
    return false;
  }
  const startDateTime = createLegacyDateTime(data, "start");
  if (!startDateTime) {
    return false;
  }
  const now = /* @__PURE__ */ new Date();
  if (!hasRequiredFields(data, "end")) {
    return isAfter(now, startDateTime);
  }
  const endDateTime = createLegacyDateTime(data, "end");
  if (!endDateTime) {
    return false;
  }
  return isAfter(now, startDateTime) && isBefore(now, endDateTime);
}
var evaluateTimeCondition = (rules) => {
  try {
    const data = rules.data || {};
    if (isTimeConditionDataV2(data)) {
      return evaluateTimeConditionV2(data);
    }
    if (isTimeConditionDataLegacy(data)) {
      return evaluateTimeConditionLegacy(data);
    }
    return false;
  } catch {
    return false;
  }
};

// src/error.ts
var isValidSelector = (selector) => {
  if (!selector) {
    return false;
  }
  if (selector.type === "auto" && !selector.selectors) {
    return false;
  }
  if (selector.type === "manual" && !selector.customSelector) {
    return false;
  }
  return true;
};
var getUserAttrError = (data, attributes) => {
  const ret = { showError: false };
  const item = attributes.find((item2) => item2.id === (data == null ? void 0 : data.attrId));
  if (!(data == null ? void 0 : data.attrId) || !item) {
    ret.showError = true;
  } else if ((data == null ? void 0 : data.logic) === "between" && (!(data == null ? void 0 : data.value) || !(data == null ? void 0 : data.value2))) {
    ret.showError = true;
  } else if ((item == null ? void 0 : item.dataType) !== AttributeDataType.Boolean) {
    if (data.logic !== "any" && data.logic !== "empty") {
      if ((item == null ? void 0 : item.dataType) === AttributeDataType.List) {
        if (!data.listValues || data.listValues.length === 0) {
          ret.showError = true;
        }
      } else {
        if (!data.value || data.value === "") {
          ret.showError = true;
        }
      }
    }
  }
  return ret;
};
var getUrlPatternError = (data) => {
  const ret = { showError: false };
  const hasExcludes = data.excludes && data.excludes.length > 0;
  const hasIncludes = data.includes && data.includes.length > 0;
  if (!hasExcludes && !hasIncludes) {
    ret.showError = true;
  }
  return ret;
};
var getCurrentTimeError = (data) => {
  const ret = { showError: false };
  if (isTimeConditionDataV2(data)) {
    if (!data.startTime && !data.endTime) {
      ret.showError = true;
    }
    return ret;
  }
  if (isTimeConditionDataLegacy(data)) {
    if (!data.startDate && !data.endDate) {
      ret.showError = true;
    }
    return ret;
  }
  if (!data || typeof data === "object" && Object.keys(data).length === 0) {
    ret.showError = true;
  }
  return ret;
};
var getWaitError = (data) => {
  const ret = { showError: false };
  if (data.second === void 0) {
    ret.showError = true;
  }
  return ret;
};
var getElementError = (data) => {
  const ret = { showError: false };
  if (!isValidSelector(data.elementData)) {
    ret.showError = true;
  }
  return ret;
};
var getTextInputError = (data) => {
  const ret = { showError: false };
  if (data.logic !== "any" && data.logic !== "empty" && data.value === "") {
    ret.showError = true;
  } else if (!isValidSelector(data.elementData)) {
    ret.showError = true;
  }
  return ret;
};
var getTextFillError = (data) => {
  const ret = { showError: false };
  if (!isValidSelector(data.elementData)) {
    ret.showError = true;
  }
  return ret;
};
var getSegmentError = (data) => {
  const ret = { showError: false };
  if (!data.segmentId) {
    ret.showError = true;
  }
  return ret;
};
var getContentError = (data) => {
  const ret = { showError: false };
  if (!data.contentId) {
    ret.showError = true;
  }
  return ret;
};
var getEventError = (data) => {
  const ret = { showError: false };
  if (!(data == null ? void 0 : data.eventId)) {
    ret.showError = true;
    return ret;
  }
  if (data.count === void 0 || data.count === null) {
    ret.showError = true;
    return ret;
  }
  if (data.countLogic === EventCountLogic.BETWEEN && (data.count2 === void 0 || data.count2 === null)) {
    ret.showError = true;
    return ret;
  }
  if (data.timeLogic !== EventTimeLogic.AT_ANY_POINT_IN_TIME && (data.windowValue === void 0 || data.windowValue === null)) {
    ret.showError = true;
    return ret;
  }
  if (data.timeLogic === EventTimeLogic.BETWEEN && (data.windowValue2 === void 0 || data.windowValue2 === null)) {
    ret.showError = true;
    return ret;
  }
  if (data.timeLogic !== EventTimeLogic.AT_ANY_POINT_IN_TIME && (data.timeUnit === void 0 || data.timeUnit === null)) {
    ret.showError = true;
    return ret;
  }
  return ret;
};
var getEventAttrError = (data, attributes) => {
  const ret = { showError: false };
  const item = attributes.find(
    (attr) => attr.id === (data == null ? void 0 : data.attrId) && attr.bizType === AttributeBizTypes.Event
  );
  if (!(data == null ? void 0 : data.attrId) || !item) {
    ret.showError = true;
  } else if ((data == null ? void 0 : data.logic) === "between" && (!(data == null ? void 0 : data.value) || !(data == null ? void 0 : data.value2))) {
    ret.showError = true;
  } else if ((item == null ? void 0 : item.dataType) !== AttributeDataType.Boolean) {
    if (data.logic !== "any" && data.logic !== "empty") {
      if ((item == null ? void 0 : item.dataType) === AttributeDataType.List) {
        if (!data.listValues || data.listValues.length === 0) {
          ret.showError = true;
        }
      } else {
        if (!data.value || data.value === "") {
          ret.showError = true;
        }
      }
    }
  }
  return ret;
};
var errorHandlerMapping = {
  "current-page": getUrlPatternError,
  "user-attr": getUserAttrError,
  time: getCurrentTimeError,
  wait: getWaitError,
  element: getElementError,
  "text-input": getTextInputError,
  "text-fill": getTextFillError,
  segment: getSegmentError,
  content: getContentError,
  event: getEventError,
  "event-attr": getEventAttrError
};
var hasError = (conds, attributes) => {
  var _a;
  for (const cond of conds) {
    const condType = cond.type;
    const condErroFn = errorHandlerMapping[condType];
    if (condErroFn) {
      const { showError } = condErroFn(cond.data, attributes);
      if (showError) {
        return true;
      }
    }
    if (cond.conditions) {
      if (hasError(cond.conditions, attributes) === true) {
        return true;
      }
    }
    if (cond.type === "event") {
      const whereConditions = (_a = cond.data) == null ? void 0 : _a.whereConditions;
      if (whereConditions && hasError(whereConditions, attributes) === true) {
        return true;
      }
    }
  }
  return false;
};
function isErrorWithMessage(error) {
  return typeof error === "object" && error !== null && "message" in error && typeof error.message === "string";
}
function toErrorWithMessage(maybeError) {
  if (isErrorWithMessage(maybeError))
    return maybeError;
  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    return new Error(String(maybeError));
  }
}
function getErrorMessage(error) {
  return toErrorWithMessage(error).message;
}

// src/globals.ts
var win = typeof window !== "undefined" ? window : void 0;
var global = typeof globalThis !== "undefined" ? globalThis : win;
var ArrayProto = Array.prototype;
var nativeForEach = ArrayProto.forEach;
var nativeIndexOf = ArrayProto.indexOf;
var navigator = global == null ? void 0 : global.navigator;
var document = global == null ? void 0 : global.document;
var location = global == null ? void 0 : global.location;
var fetch = global == null ? void 0 : global.fetch;
var XMLHttpRequest = (global == null ? void 0 : global.XMLHttpRequest) && "withCredentials" in new global.XMLHttpRequest() ? global.XMLHttpRequest : void 0;
var AbortController = global == null ? void 0 : global.AbortController;
var userAgent = navigator == null ? void 0 : navigator.userAgent;
var assignableWindow = win != null ? win : {};

// src/storage.ts
var AppStorage = class {
  constructor(prefix, defaultExpire) {
    __publicField(this, "prefix", "USERTOUR@0.0.1");
    __publicField(this, "defaultExpire", 5);
    __publicField(this, "setStorage", (type, key, inputValue, inputExpire = -1) => {
      var _a, _b;
      const value = inputValue === "" || inputValue === void 0 ? null : inputValue;
      const expire = Number.isNaN(inputExpire) ? this.defaultExpire : inputExpire;
      const data = {
        value,
        time: Date.now(),
        expire
      };
      try {
        const stringifyData = JSON.stringify(data);
        (_b = (_a = win) == null ? void 0 : _a[type]) == null ? void 0 : _b.setItem(this.autoAddPrefix(key), stringifyData);
      } catch (e) {
        console.error(`[APP_STORAGE] setStorage error: ${e}`);
      }
    });
    __publicField(this, "getStorage", (type, key) => {
      var _a, _b, _c, _d;
      const prefixKey = this.autoAddPrefix(key);
      if (!((_b = (_a = win) == null ? void 0 : _a[type]) == null ? void 0 : _b.getItem(prefixKey))) {
        return void 0;
      }
      let storage2 = void 0;
      try {
        const stringifyData = (_d = (_c = win) == null ? void 0 : _c[type]) == null ? void 0 : _d.getItem(prefixKey);
        if (stringifyData === null || stringifyData === void 0) {
          return void 0;
        }
        storage2 = JSON.parse(stringifyData);
      } catch (e) {
        console.error(`[APP_STORAGE] getStorage error: ${e}`);
        return void 0;
      }
      const nowTime = Date.now();
      if (storage2 === void 0) {
        return void 0;
      }
      if (storage2.expire > -1 && storage2.expire * 60 * 1e3 < nowTime - storage2.time) {
        this.removeStorage(type, key);
        return void 0;
      }
      this.setStorage(type, key, storage2.value, storage2.expire);
      return storage2.value;
    });
    __publicField(this, "clearStorage", (type) => {
      var _a, _b;
      (_b = (_a = win) == null ? void 0 : _a[type]) == null ? void 0 : _b.clear();
    });
    __publicField(this, "removeStorage", (type, key) => {
      var _a, _b;
      (_b = (_a = win) == null ? void 0 : _a[type]) == null ? void 0 : _b.removeItem(this.autoAddPrefix(key));
    });
    __publicField(this, "setLocalStorage", (key, value, expire) => {
      this.setStorage("localStorage", key, value, expire);
    });
    __publicField(this, "getLocalStorage", (key) => {
      return this.getStorage("localStorage", key);
    });
    __publicField(this, "clearLocalStorage", () => {
      this.clearStorage("localStorage");
    });
    __publicField(this, "removeLocalStorage", (key) => {
      this.removeStorage("localStorage", key);
    });
    __publicField(this, "setSessionStorage", (key, value, expire) => {
      this.setStorage("sessionStorage", key, value, expire);
    });
    __publicField(this, "getSessionStorage", (key) => {
      return this.getStorage("sessionStorage", key);
    });
    __publicField(this, "clearSessionStorage", () => {
      this.clearStorage("sessionStorage");
    });
    __publicField(this, "removeSessionStorage", (key) => {
      this.removeStorage("sessionStorage", key);
    });
    if (prefix !== void 0) {
      this.prefix = prefix;
    }
    if (defaultExpire !== void 0) {
      this.defaultExpire = defaultExpire;
    }
  }
  autoAddPrefix(key) {
    return `${this.prefix}/${key}`;
  }
};

// src/auth.ts
var storage = new AppStorage("USERTOUR@0.0.1", 5);
var TOKEN_NAME = "token";
var getAuthToken = () => {
  return storage.getLocalStorage(TOKEN_NAME);
};
var removeAuthToken = () => {
  return storage.removeLocalStorage(TOKEN_NAME);
};
var setAuthToken = (token, expire) => {
  storage.setLocalStorage(TOKEN_NAME, token, expire);
};

// src/settings.ts
import { ContentEditorElementType } from "@usertour/types";
var defaultColumn = {
  type: ContentEditorElementType.COLUMN,
  width: { type: "fill" },
  justifyContent: "justify-center"
};
var defaultStep = {
  name: "step",
  type: "tooltip",
  sequence: 0,
  setting: {
    height: 0,
    width: void 0,
    // Auto - use theme default width
    skippable: true,
    enabledBackdrop: false,
    enabledBlockTarget: false,
    explicitCompletionStep: false,
    align: "center",
    side: "bottom",
    sideOffset: 0,
    alignOffset: 0,
    alignType: "auto",
    position: "center",
    positionOffsetX: 0,
    positionOffsetY: 0
  },
  data: [
    {
      type: "paragraph",
      children: [{ text: "" }]
    }
  ],
  target: {
    selectors: null,
    content: "",
    sequence: "1st",
    precision: "stricter",
    isDynamicContent: false,
    customSelector: "",
    type: "auto"
  }
};

// src/is-url.ts
var protocolAndDomainRE = /^(?:\w+:)?\/\/(\S+)$/;
var localhostDomainRE = /^localhost[\:?\d]*(?:[^\:?\d]\S*)?$/;
var nonLocalhostDomainRE = /^[^\s\.]+\.\S{2,}$/;
function isUrl(string) {
  if (typeof string !== "string") {
    return false;
  }
  const match = string.match(protocolAndDomainRE);
  if (!match) {
    return false;
  }
  const everythingAfterProtocol = match[1];
  if (!everythingAfterProtocol) {
    return false;
  }
  if (localhostDomainRE.test(everythingAfterProtocol) || nonLocalhostDomainRE.test(everythingAfterProtocol)) {
    return true;
  }
  return false;
}

// src/capability-matrix.ts
import {
  ContentActionsItemType,
  ContentDataType,
  RulesType,
  StepContentType
} from "@usertour/types";
var SERVER_EVALUATED_CONDITION_TYPES = [
  RulesType.EVENT,
  RulesType.SEGMENT,
  RulesType.CONTENT
];
var CONTENT_ACTION_CAPABILITIES = {
  [ContentDataType.FLOW]: {
    actions: [
      ContentActionsItemType.STEP_GOTO,
      ContentActionsItemType.FLOW_DISMIS,
      ContentActionsItemType.FLOW_START,
      ContentActionsItemType.PAGE_NAVIGATE,
      ContentActionsItemType.JAVASCRIPT_EVALUATE
    ],
    dismissVariant: ContentActionsItemType.FLOW_DISMIS
  },
  [ContentDataType.CHECKLIST]: {
    actions: [
      ContentActionsItemType.CHECKLIST_DISMIS,
      ContentActionsItemType.FLOW_START,
      ContentActionsItemType.PAGE_NAVIGATE,
      ContentActionsItemType.JAVASCRIPT_EVALUATE
    ],
    dismissVariant: ContentActionsItemType.CHECKLIST_DISMIS
  },
  [ContentDataType.LAUNCHER]: {
    actions: [
      ContentActionsItemType.LAUNCHER_DISMIS,
      ContentActionsItemType.JAVASCRIPT_EVALUATE,
      ContentActionsItemType.PAGE_NAVIGATE,
      ContentActionsItemType.FLOW_START
    ],
    dismissVariant: ContentActionsItemType.LAUNCHER_DISMIS
  },
  [ContentDataType.BANNER]: {
    actions: [
      ContentActionsItemType.BANNER_DISMIS,
      ContentActionsItemType.FLOW_START,
      ContentActionsItemType.PAGE_NAVIGATE,
      ContentActionsItemType.JAVASCRIPT_EVALUATE
    ],
    dismissVariant: ContentActionsItemType.BANNER_DISMIS
  },
  [ContentDataType.RESOURCE_CENTER]: {
    actions: [
      ContentActionsItemType.FLOW_START,
      ContentActionsItemType.PAGE_NAVIGATE,
      ContentActionsItemType.JAVASCRIPT_EVALUATE
    ],
    dismissVariant: null
  },
  [ContentDataType.TRACKER]: {
    // Headless — no content body, no action slots at all.
    actions: [],
    dismissVariant: null
  },
  [ContentDataType.ANNOUNCEMENT]: {
    // Mirrors the announcement editor's action picker (content-detail-
    // announcement-editor). Feed items are marked seen, not dismissed — no
    // dismiss variant.
    actions: [
      ContentActionsItemType.FLOW_START,
      ContentActionsItemType.PAGE_NAVIGATE,
      ContentActionsItemType.JAVASCRIPT_EVALUATE
    ],
    dismissVariant: null
  }
};
var STEP_CAPABILITIES = {
  [StepContentType.TOOLTIP]: { placement: "anchor", onClick: true, requiresTarget: true },
  [StepContentType.MODAL]: { placement: "grid", onClick: false, requiresTarget: false },
  [StepContentType.BUBBLE]: { placement: "theme", onClick: false, requiresTarget: false },
  [StepContentType.HIDDEN]: { placement: "none", onClick: false, requiresTarget: false }
};
var CONTENT_REFERENCE_TARGET_TYPES = [
  ContentDataType.FLOW,
  ContentDataType.CHECKLIST
];
var CONTENT_TYPE_TRAITS = {
  [ContentDataType.FLOW]: {
    requiresTheme: true,
    autoStartRequiredToAppear: false,
    startsOnDemand: true,
    allowsScheduledAt: false
  },
  [ContentDataType.CHECKLIST]: {
    requiresTheme: true,
    autoStartRequiredToAppear: false,
    startsOnDemand: true,
    allowsScheduledAt: false
  },
  [ContentDataType.LAUNCHER]: {
    requiresTheme: true,
    autoStartRequiredToAppear: true,
    startsOnDemand: false,
    allowsScheduledAt: false
  },
  [ContentDataType.BANNER]: {
    requiresTheme: true,
    autoStartRequiredToAppear: true,
    startsOnDemand: false,
    allowsScheduledAt: false
  },
  [ContentDataType.RESOURCE_CENTER]: {
    requiresTheme: true,
    autoStartRequiredToAppear: true,
    startsOnDemand: false,
    allowsScheduledAt: false
  },
  [ContentDataType.TRACKER]: {
    requiresTheme: false,
    autoStartRequiredToAppear: false,
    startsOnDemand: false,
    allowsScheduledAt: false
  },
  [ContentDataType.ANNOUNCEMENT]: {
    requiresTheme: true,
    autoStartRequiredToAppear: false,
    startsOnDemand: false,
    allowsScheduledAt: true
  }
};

// src/content.ts
import {
  ContentEditorElementType as ContentEditorElementType2,
  ContentDataType as ContentDataType2,
  ContentPriority,
  Frequency,
  FrequencyUnits,
  RulesType as RulesType2
} from "@usertour/types";
import { deepmerge } from "deepmerge-ts";
var isPublishedInAllEnvironments = (content, environmentList, version) => {
  var _a;
  if (!((_a = content == null ? void 0 : content.contentOnEnvironments) == null ? void 0 : _a.length) || !(environmentList == null ? void 0 : environmentList.length) || !(version == null ? void 0 : version.id)) {
    return false;
  }
  return environmentList.every(
    (env) => {
      var _a2;
      return (_a2 = content == null ? void 0 : content.contentOnEnvironments) == null ? void 0 : _a2.some(
        (item) => item.published && item.publishedVersionId === version.id && item.environment.id === env.id
      );
    }
  );
};
var isPublishedAtLeastOneEnvironment = (content) => {
  var _a;
  if ((content == null ? void 0 : content.contentOnEnvironments) && ((_a = content == null ? void 0 : content.contentOnEnvironments) == null ? void 0 : _a.length) > 0) {
    return true;
  }
  return false;
};
var rulesSetting = {
  startIfNotComplete: false,
  priority: ContentPriority.MEDIUM,
  wait: 0
};
var FREQUENCY_DEFAULT_TYPES = [ContentDataType2.FLOW, ContentDataType2.CHECKLIST];
var DEFAULT_FREQUENCY = {
  frequency: Frequency.ONCE,
  every: { times: 2, duration: 1, unit: FrequencyUnits.DAYES },
  atLeast: { duration: 0, unit: FrequencyUnits.MINUTES }
};
var defaultFrequencyFor = (contentType) => contentType === ContentDataType2.CHECKLIST ? { frequency: DEFAULT_FREQUENCY.frequency, every: DEFAULT_FREQUENCY.every } : DEFAULT_FREQUENCY;
var hideRulesSetting = {};
var defaultContentConfig = {
  enabledAutoStartRules: false,
  enabledHideRules: false,
  autoStartRules: [],
  hideRules: [],
  autoStartRulesSetting: rulesSetting,
  hideRulesSetting
};
var withFrequencyDefault = (setting, contentType) => {
  if (setting.frequency) {
    return setting.frequency.every ? setting : { ...setting, frequency: { ...setting.frequency, every: DEFAULT_FREQUENCY.every } };
  }
  return !contentType || !FREQUENCY_DEFAULT_TYPES.includes(contentType) ? setting : { ...setting, frequency: defaultFrequencyFor(contentType) };
};
var buildConfig = (config, contentType) => ({
  ...defaultContentConfig,
  ...config,
  autoStartRulesSetting: withFrequencyDefault(
    deepmerge(rulesSetting, (config == null ? void 0 : config.autoStartRulesSetting) || {}),
    contentType
  ),
  hideRulesSetting: (config == null ? void 0 : config.hideRulesSetting) || {}
});
var NO_AUTO_START_CAPABILITIES = {
  frequency: false,
  atLeast: false,
  ifCompleted: false,
  wait: false,
  priority: false,
  hideRules: false
};
var AUTO_START_CAPABILITIES = {
  [ContentDataType2.FLOW]: {
    frequency: true,
    atLeast: true,
    ifCompleted: true,
    wait: true,
    priority: true,
    hideRules: true
  },
  [ContentDataType2.CHECKLIST]: {
    frequency: true,
    atLeast: false,
    ifCompleted: true,
    wait: true,
    priority: true,
    hideRules: true
  },
  [ContentDataType2.LAUNCHER]: { ...NO_AUTO_START_CAPABILITIES },
  // Banner is a SINGLETON (one shows at a time) but was the only singleton with
  // no priority: two banners whose rules overlap left the author no way to pick
  // the winner — the runtime falls back to query order. It already sorts
  // candidates with priorityCompare, so exposing the knob is all that was
  // missing. The rest stays off: a banner is re-evaluated every page, so
  // frequency / wait / if-not-complete / hide rules have nothing to act on.
  [ContentDataType2.BANNER]: { ...NO_AUTO_START_CAPABILITIES, priority: true },
  // Announcements are a FEED, not a popup: their `when` rules act purely as an
  // audience filter for feed inclusion (announcement.service filterByTargeting);
  // none of the session-start knobs (frequency / wait / priority / …) apply, and
  // the filter conditions are user-attribute / segment only.
  [ContentDataType2.ANNOUNCEMENT]: {
    ...NO_AUTO_START_CAPABILITIES,
    startConditionTypes: [RulesType2.USER_ATTR, RulesType2.SEGMENT]
  },
  [ContentDataType2.RESOURCE_CENTER]: {
    ...NO_AUTO_START_CAPABILITIES,
    priority: true,
    hideRules: true
  },
  [ContentDataType2.TRACKER]: {
    ...NO_AUTO_START_CAPABILITIES,
    clientConditionsOnly: true
  }
};
var getAutoStartCapabilities = (contentType) => contentType && AUTO_START_CAPABILITIES[contentType] || NO_AUTO_START_CAPABILITIES;
var extractUserAttributeValue = (element, userAttributes) => {
  if (!userAttributes || !("attrCode" in element) || typeof element.attrCode !== "string") {
    return "fallback" in element && typeof element.fallback === "string" ? element.fallback : "";
  }
  const attrValue = Object.prototype.hasOwnProperty.call(userAttributes, element.attrCode) ? userAttributes[element.attrCode] : void 0;
  const fallback = "fallback" in element && typeof element.fallback === "string" ? element.fallback : "";
  return attrValue != null ? attrValue : fallback;
};
var serializeLinkTemplate = (value, resolveChip) => {
  let url = "";
  try {
    for (const v of value) {
      if (v && "children" in v && Array.isArray(v.children)) {
        for (const vc of v.children) {
          if (vc && "type" in vc && vc.type === "user-attribute") {
            const resolved = resolveChip(vc);
            if (resolved === void 0) {
              return void 0;
            }
            url += resolved;
          } else if (vc && "text" in vc && typeof vc.text === "string") {
            url += vc.text;
          }
        }
      }
    }
  } catch (_) {
  }
  return url;
};
var extractLinkUrl = (value, userAttributes) => {
  var _a;
  return (_a = serializeLinkTemplate(
    value,
    (chip) => String(extractUserAttributeValue(chip, userAttributes))
  )) != null ? _a : "";
};
var toStoredUrl = (value) => {
  return typeof value === "string" ? value : "";
};
var replaceUserAttrForElement = (data, userAttributes) => {
  return data.map((v) => {
    if (v.children) {
      v.children = replaceUserAttrForElement(v.children, userAttributes);
    }
    if (v.type === "user-attribute" && userAttributes) {
      const value = extractUserAttributeValue(v, userAttributes);
      if (!isUndefined(value)) {
        v.value = String(value);
      }
    }
    if (v.type === "link" && userAttributes) {
      v.url = v.data ? extractLinkUrl(v.data, userAttributes) : toStoredUrl(v.url);
    }
    return v;
  });
};
var replaceUserAttr = (editorContents, userAttributes) => {
  return editorContents.map((editorContent) => {
    if (!editorContent.children) {
      return editorContent;
    }
    return {
      ...editorContent,
      children: editorContent.children.map((column) => {
        if (!column.children) {
          return column;
        }
        return {
          ...column,
          children: column.children.map((element) => {
            if (element.element.type === ContentEditorElementType2.TEXT) {
              return {
                ...element,
                element: {
                  ...element.element,
                  data: replaceUserAttrForElement(element.element.data, userAttributes)
                }
              };
            }
            if (element.element.type === ContentEditorElementType2.IMAGE) {
              const link = element.element.link;
              if (link == null ? void 0 : link.data) {
                return {
                  ...element,
                  element: {
                    ...element.element,
                    link: {
                      ...link,
                      url: extractLinkUrl(link.data, userAttributes)
                    }
                  }
                };
              }
            }
            return { ...element };
          })
        };
      })
    };
  });
};
var serializeBlockName = (name, userAttributes) => {
  if (!name)
    return "";
  if (typeof name === "string")
    return name;
  const extract = (node) => {
    if ("text" in node)
      return node.text;
    if (node.type === "user-attribute") {
      return String(extractUserAttributeValue(node, userAttributes));
    }
    return node.children.map(extract).join("");
  };
  return name.map(extract).join("");
};

// src/helper.ts
import { v4 } from "uuid";
import { createId } from "@paralleldrive/cuid2";
var cuid = () => {
  return createId();
};
function formatDate(input) {
  const date = new Date(input);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}
function absoluteUrl(path) {
  return `${path}`;
}
var uuidV4 = () => {
  return v4();
};
function hexToRgb2(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: Number.parseInt(result[1], 16),
    g: Number.parseInt(result[2], 16),
    b: Number.parseInt(result[3], 16)
  } : null;
}
var executeUserCode = (code) => {
  try {
    return new Function(code)();
  } catch (error) {
    console.error("Usertour.js: Error executing user code:", error);
    return null;
  }
};
var getRandomColor = () => {
  const colors = [
    "bg-red-500",
    "bg-orange-500",
    "bg-yellow-500",
    "bg-green-500",
    "bg-teal-500",
    "bg-blue-500",
    "bg-indigo-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-rose-500"
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};
var needsDarkText = (hex) => {
  const rgb = hexToRgb2(hex);
  if (!rgb) {
    return false;
  }
  const { r, g, b } = rgb;
  return r * 0.299 + g * 0.587 + b * 0.114 > 186;
};
var isNearWhite = (hex) => {
  const rgb = hexToRgb2(hex);
  if (!rgb) {
    return false;
  }
  const { r, g, b } = rgb;
  return r * 0.299 + g * 0.587 + b * 0.114 > 245;
};
var firstLetterToUpperCase = (word) => {
  if (!word)
    return word;
  return word.charAt(0).toUpperCase() + word.slice(1);
};

// src/conditions/condition.ts
import { RulesType as RulesType3 } from "@usertour/types";
import isEqual from "fast-deep-equal";

// src/conditions/url-v2.ts
function parseUrl(url) {
  const match = url.match(/^(([a-z\d]+):\/\/)?([^/?#]+)?(\/[^?#]*)?(\?([^#]*))?(#.*)?$/i);
  if (!match)
    return null;
  const [, , scheme, domain, path, , query, fragment] = match;
  return {
    scheme: scheme || "",
    domain: domain || "",
    path: path || "",
    query: query || "",
    fragment: fragment || ""
  };
}
function escapeRegex(str) {
  return str.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
}
function processUrlPattern(pattern, wildcardReplacement, paramReplacement) {
  let processed = escapeRegex(pattern);
  processed = processed.replace(/\\\*/g, `${wildcardReplacement}*`);
  if (paramReplacement) {
    processed = processed.replace(/:[a-zA-Z0-9_]+/g, `[^${paramReplacement}]+`);
  }
  return processed;
}
function urlPatternToRegex(pattern) {
  const parsed = parseUrl(pattern);
  if (!parsed) {
    return null;
  }
  const { scheme, domain, path, query, fragment } = parsed;
  const schemePattern = scheme ? escapeRegex(scheme) : "[a-z\\d]+";
  const domainPattern = domain ? processUrlPattern(domain, "[^/]", ".") : "[^/]*";
  const portPattern = "(:\\d+)?";
  const pathPattern = path ? processUrlPattern(path, "[^?#]", "/") : "/[^?#]*";
  let queryPattern;
  if (query) {
    queryPattern = "";
    new URLSearchParams(query).forEach((value, key) => {
      let valuePattern;
      if (value === "") {
        valuePattern = "=?";
      } else if (value === "*") {
        valuePattern = "(=[^&#]*)?";
      } else {
        const encodedValue = value.split(/\*/g).map((part) => encodeURI(part)).join("*");
        valuePattern = `=${processUrlPattern(encodedValue, "[^#]")}`;
      }
      queryPattern += `(?=.*[?&]${escapeRegex(key)}${valuePattern}([&#]|$))`;
    });
    queryPattern += "\\?[^#]*";
  } else {
    queryPattern = "(\\?[^#]*)?";
  }
  const fragmentPattern = fragment ? processUrlPattern(fragment, ".", "/") : "(#.*)?";
  return new RegExp(
    `^${schemePattern}://${domainPattern}${portPattern}${pathPattern}${queryPattern}${fragmentPattern}$`
  );
}
function matchUrlPattern(urlPattern, url) {
  if (urlPattern.includes.length === 0 && urlPattern.excludes.length === 0) {
    return false;
  }
  const matchesInclude = urlPattern.includes.length === 0 || urlPattern.includes.some((includePattern) => {
    const regex = urlPatternToRegex(includePattern);
    return regex && url.match(regex);
  });
  const matchesExclude = urlPattern.excludes.some((excludePattern) => {
    const regex = urlPatternToRegex(excludePattern);
    return regex && url.match(regex);
  });
  return matchesInclude && !matchesExclude;
}

// src/conditions/url.ts
var isMatchUrlPattern = (_url, includes, excludes) => {
  return matchUrlPattern({ includes, excludes }, _url);
};
var evaluateUrlCondition = (rules, url) => {
  const { excludes = [], includes = [] } = rules.data || {};
  return isMatchUrlPattern(url, includes, excludes);
};

// src/conditions/attribute.ts
import {
  BizAttributeTypes as BizAttributeTypes2,
  AttributeBizTypes as AttributeBizTypes2
} from "@usertour/types";
import { subDays, startOfDay, endOfDay } from "date-fns";

// src/bucketing.ts
import {
  RANDOM_AB_VALUES,
  RANDOM_NUMBER_RANGE_MAX,
  RANDOM_NUMBER_RANGE_MIN
} from "@usertour/constants";
import { BizAttributeTypes } from "@usertour/types";
var isBucketingDataType = (dataType) => {
  return dataType === BizAttributeTypes.RandomAB || dataType === BizAttributeTypes.RandomNumber;
};
var effectiveDataType = (dataType) => {
  if (dataType === BizAttributeTypes.RandomAB) {
    return BizAttributeTypes.String;
  }
  if (dataType === BizAttributeTypes.RandomNumber) {
    return BizAttributeTypes.Number;
  }
  return dataType;
};
var isValidRandomMax = (randomMax) => {
  return typeof randomMax === "number" && Number.isInteger(randomMax) && randomMax >= RANDOM_NUMBER_RANGE_MIN && randomMax <= RANDOM_NUMBER_RANGE_MAX;
};
var hash32 = (input) => {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 2246822507);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 3266489909);
  hash ^= hash >>> 16;
  return hash >>> 0;
};
var bucketPoint = (attributeId, externalId) => {
  return hash32(`${attributeId}\0${externalId}`) / 4294967296;
};
var bucketValue = (attribute, externalId) => {
  if (attribute.dataType === BizAttributeTypes.RandomAB) {
    const point = bucketPoint(attribute.id, externalId);
    return RANDOM_AB_VALUES[point < 0.5 ? 0 : 1];
  }
  if (attribute.dataType === BizAttributeTypes.RandomNumber) {
    const max = isValidRandomMax(attribute.randomMax) ? attribute.randomMax : RANDOM_NUMBER_RANGE_MIN;
    return 1 + Math.floor(bucketPoint(attribute.id, externalId) * max);
  }
  return void 0;
};
var missingBucketValues = (attributes, externalId, stored) => {
  const values = {};
  for (const attribute of attributes) {
    if (Object.prototype.hasOwnProperty.call(stored, attribute.codeName)) {
      continue;
    }
    const value = bucketValue(attribute, externalId);
    if (value !== void 0) {
      values[attribute.codeName] = value;
    }
  }
  return values;
};

// src/conditions/attribute.ts
function evaluateAttributeCondition(condition, options) {
  const { data } = condition;
  if (!data) {
    return false;
  }
  const { logic, value, attrId, value2, listValues = [] } = data;
  const {
    attributes,
    userAttributes = {},
    companyAttributes = {},
    membershipAttributes = {}
  } = options;
  if (!attrId || !attributes) {
    return false;
  }
  const attr = attributes.find((attr2) => attr2.id === attrId);
  if (!attr) {
    return false;
  }
  const bizAttributes = attr.bizType === AttributeBizTypes2.Company ? companyAttributes : attr.bizType === AttributeBizTypes2.Membership ? membershipAttributes : userAttributes;
  const actualValue = getAttributeValue(attr.codeName, bizAttributes);
  const dataType = effectiveDataType(attr.dataType);
  if (dataType === BizAttributeTypes2.String) {
    return evaluateStringCondition(logic, actualValue, value);
  }
  if (dataType === BizAttributeTypes2.Number) {
    return evaluateNumberCondition(logic, actualValue, value, value2);
  }
  if (dataType === BizAttributeTypes2.Boolean) {
    return evaluateBooleanCondition(logic, actualValue);
  }
  if (dataType === BizAttributeTypes2.List) {
    return evaluateListCondition(logic, actualValue, listValues);
  }
  if (dataType === BizAttributeTypes2.DateTime) {
    return evaluateDateTimeCondition(logic, actualValue, value);
  }
  return false;
}
function getAttributeValue(codeName, attributes) {
  if (!attributes || !Object.prototype.hasOwnProperty.call(attributes, codeName)) {
    return void 0;
  }
  return attributes[codeName];
}
function evaluateStringCondition(logic, actualValue, expectedValue) {
  const stringValue = actualValue === null || actualValue === void 0 ? "" : String(actualValue);
  switch (logic) {
    case "is":
      return stringValue === expectedValue;
    case "not":
      return stringValue !== expectedValue;
    case "contains":
      return stringValue.includes(expectedValue);
    case "notContain":
      return !stringValue.includes(expectedValue);
    case "startsWith":
      return stringValue.startsWith(expectedValue);
    case "endsWith":
      return stringValue.endsWith(expectedValue);
    case "empty": {
      const isEmpty2 = !stringValue || stringValue === "";
      return isEmpty2;
    }
    case "any":
      return Boolean(stringValue && stringValue !== "");
    default:
      return false;
  }
}
function evaluateNumberCondition(logic, actualValue, expectedValue, expectedValue2) {
  const hasValue = actualValue !== null && actualValue !== void 0 && actualValue !== "";
  if (logic === "empty") {
    return !hasValue;
  }
  if (logic === "any") {
    return hasValue;
  }
  if (!hasValue) {
    return logic === "not";
  }
  const numActualValue = Number(actualValue);
  const numExpectedValue = Number(expectedValue);
  const numExpectedValue2 = Number(expectedValue2);
  if (Number.isNaN(numActualValue)) {
    return false;
  }
  switch (logic) {
    case "is":
      return numActualValue === numExpectedValue;
    case "not":
      return numActualValue !== numExpectedValue;
    case "isLessThan":
      return numActualValue < numExpectedValue;
    case "isLessThanOrEqualTo":
      return numActualValue <= numExpectedValue;
    case "isGreaterThan":
      return numActualValue > numExpectedValue;
    case "isGreaterThanOrEqualTo":
      return numActualValue >= numExpectedValue;
    case "between":
      return numActualValue >= numExpectedValue && numActualValue <= numExpectedValue2;
    default:
      return false;
  }
}
function evaluateBooleanCondition(logic, actualValue) {
  switch (logic) {
    case "true":
      return actualValue === true;
    case "false":
      return actualValue === false;
    case "empty":
      return actualValue === null || actualValue === void 0 || actualValue === "";
    case "any":
      return actualValue !== null && actualValue !== void 0 && actualValue !== "";
    default:
      return false;
  }
}
function evaluateListCondition(logic, actualValue, expectedValues) {
  const arrayValue = isArray(actualValue) ? actualValue : [];
  if (logic === "empty" || logic === "any") {
    switch (logic) {
      case "empty":
        return !arrayValue || arrayValue.length === 0;
      case "any":
        return arrayValue && arrayValue.length > 0;
      default:
        return false;
    }
  }
  const filteredValues = expectedValues.filter(
    (value) => value !== null && value !== void 0 && value !== ""
  );
  if (!filteredValues.length) {
    return false;
  }
  switch (logic) {
    case "includesAtLeastOne":
      return filteredValues.some((value) => arrayValue.includes(value));
    case "includesAll":
      return filteredValues.every((value) => arrayValue.includes(value));
    case "notIncludesAtLeastOne":
      return filteredValues.some((value) => !arrayValue.includes(value));
    case "notIncludesAll":
      return filteredValues.every((value) => !arrayValue.includes(value));
    default:
      return false;
  }
}
function evaluateDateTimeCondition(logic, actualValue, expectedValue) {
  const actualDate = actualValue ? new Date(actualValue) : null;
  const now = /* @__PURE__ */ new Date();
  if (logic === "empty") {
    return !actualValue || actualValue === "";
  }
  if (logic === "any") {
    return Boolean(actualValue) && actualValue !== "";
  }
  if (!actualDate || Number.isNaN(actualDate.getTime())) {
    return false;
  }
  switch (logic) {
    case "lessThan": {
      const targetDate = subDays(now, Number(expectedValue));
      return actualDate >= targetDate;
    }
    case "exactly": {
      const targetDate = subDays(now, Number(expectedValue));
      const start = startOfDay(targetDate);
      const end = endOfDay(targetDate);
      return actualDate >= start && actualDate <= end;
    }
    case "moreThan": {
      const targetDate = subDays(now, Number(expectedValue));
      return actualDate <= targetDate;
    }
    case "before": {
      const expectedDate = new Date(expectedValue);
      return actualDate <= expectedDate;
    }
    case "on": {
      const expectedDateOn = new Date(expectedValue);
      const start = startOfDay(expectedDateOn);
      const end = endOfDay(expectedDateOn);
      return actualDate >= start && actualDate <= end;
    }
    case "after": {
      const expectedDateAfter = new Date(expectedValue);
      return actualDate >= expectedDateAfter;
    }
    default:
      return false;
  }
}

// src/conditions/condition.ts
var conditionsIsSame = (rr1, rr2) => {
  return isEqual(JSON.parse(JSON.stringify(rr1)), JSON.parse(JSON.stringify(rr2)));
};
var isConditionsActived = (conditions) => {
  if (!conditions || conditions.length === 0) {
    return false;
  }
  const operator = conditions[0].operators;
  const actives = conditions.filter((rule) => {
    if (!rule.conditions) {
      return rule.actived;
    }
    return isConditionsActived(rule.conditions);
  });
  return operator === "and" ? actives.length === conditions.length : actives.length > 0;
};
var filterConditionsByType = (conditions, allowedTypes) => {
  return conditions.filter((condition) => {
    if (condition.type === "group" && condition.conditions) {
      const filteredGroupConditions = filterConditionsByType(condition.conditions, allowedTypes);
      return filteredGroupConditions.length > 0;
    }
    return allowedTypes.includes(condition.type);
  }).map((condition) => {
    if (condition.type === "group" && condition.conditions) {
      return {
        ...condition,
        conditions: filterConditionsByType(condition.conditions, allowedTypes)
      };
    }
    return condition;
  });
};
var evaluateRule = async (condition, options) => {
  var _a, _b;
  const { typeControl = {}, activatedIds, deactivatedIds, customEvaluators } = options;
  const conditionId = condition.id;
  if (conditionId && (activatedIds == null ? void 0 : activatedIds.includes(conditionId)))
    return true;
  if (conditionId && (deactivatedIds == null ? void 0 : deactivatedIds.includes(conditionId)))
    return false;
  const customEvaluator = customEvaluators == null ? void 0 : customEvaluators[condition.type];
  if (customEvaluator) {
    const result = customEvaluator(condition, options);
    return typeof result === "object" && result !== null && "then" in result ? await result : result;
  }
  if (typeControl[condition.type] !== true) {
    return condition.actived || false;
  }
  switch (condition.type) {
    case RulesType3.CURRENT_PAGE:
      return evaluateUrlCondition(condition, (_b = (_a = options.clientContext) == null ? void 0 : _a.pageUrl) != null ? _b : "");
    case RulesType3.TIME:
      return evaluateTimeCondition(condition);
    case RulesType3.USER_ATTR:
      return evaluateAttributeCondition(condition, options);
    default:
      return condition.actived || false;
  }
};
var evaluateRulesConditions = async (conditions, options = {}) => {
  const results = [];
  for (const condition of conditions) {
    if (condition.type === "group" && condition.conditions) {
      results.push({
        ...condition,
        conditions: await evaluateRulesConditions(condition.conditions, options)
      });
    } else {
      results.push({
        ...condition,
        actived: await evaluateRule(condition, options)
      });
    }
  }
  return results;
};
var regenerateConditionIds = (conditions) => {
  return conditions.map((condition) => ({
    ...condition,
    id: cuid(),
    conditions: condition.conditions ? regenerateConditionIds(condition.conditions) : void 0
  }));
};
var assignConditionIds = (conditions) => {
  return conditions.map((condition) => {
    var _a;
    return {
      ...condition,
      id: (_a = condition.id) != null ? _a : cuid(),
      conditions: condition.conditions ? assignConditionIds(condition.conditions) : void 0
    };
  });
};
var allConditionsHaveIds = (conditions) => {
  if (!conditions || conditions.length === 0)
    return true;
  for (const condition of conditions) {
    if (!condition.id) {
      return false;
    }
    if (condition.type === "group" && condition.conditions) {
      if (!allConditionsHaveIds(condition.conditions)) {
        return false;
      }
    }
  }
  return true;
};

// src/conditions/operator-mappings.ts
import { AttributeDataType as AttributeDataType2 } from "@usertour/types";
var NUMBER_OPERATORS = [
  { value: "is", labelKey: "conditions.operators.is" },
  { value: "not", labelKey: "conditions.operators.isNot" },
  { value: "isLessThan", labelKey: "conditions.operators.isLessThan" },
  { value: "isLessThanOrEqualTo", labelKey: "conditions.operators.isLessThanOrEqualTo" },
  { value: "isGreaterThan", labelKey: "conditions.operators.isGreaterThan" },
  { value: "isGreaterThanOrEqualTo", labelKey: "conditions.operators.isGreaterThanOrEqualTo" },
  { value: "between", labelKey: "conditions.operators.isBetween" },
  { value: "any", labelKey: "conditions.operators.hasAnyValue" },
  { value: "empty", labelKey: "conditions.operators.isEmpty" }
];
var OPERATORS_BY_DATATYPE = {
  [AttributeDataType2.Number]: NUMBER_OPERATORS,
  [AttributeDataType2.String]: [
    { value: "is", labelKey: "conditions.operators.is" },
    { value: "not", labelKey: "conditions.operators.isNot" },
    { value: "contains", labelKey: "conditions.operators.contains" },
    { value: "notContain", labelKey: "conditions.operators.doesNotContain" },
    { value: "startsWith", labelKey: "conditions.operators.startsWith" },
    { value: "endsWith", labelKey: "conditions.operators.endsWith" },
    { value: "any", labelKey: "conditions.operators.hasAnyValue" },
    { value: "empty", labelKey: "conditions.operators.isEmpty" }
  ],
  [AttributeDataType2.Boolean]: [
    { value: "true", labelKey: "conditions.operators.isTrue" },
    { value: "false", labelKey: "conditions.operators.isFalse" },
    { value: "any", labelKey: "conditions.operators.hasAnyValue" },
    { value: "empty", labelKey: "conditions.operators.isEmpty" }
  ],
  [AttributeDataType2.List]: [
    { value: "includesAtLeastOne", labelKey: "conditions.operators.includesAtLeastOne" },
    { value: "includesAll", labelKey: "conditions.operators.includesAll" },
    { value: "notIncludesAtLeastOne", labelKey: "conditions.operators.notIncludesAtLeastOne" },
    { value: "notIncludesAll", labelKey: "conditions.operators.notIncludesAll" },
    { value: "any", labelKey: "conditions.operators.hasAnyValue" },
    { value: "empty", labelKey: "conditions.operators.isEmpty" }
  ],
  [AttributeDataType2.DateTime]: [
    { value: "lessThan", labelKey: "conditions.operators.daysAgoLessThan" },
    { value: "exactly", labelKey: "conditions.operators.daysAgoExactly" },
    { value: "moreThan", labelKey: "conditions.operators.daysAgoMoreThan" },
    {
      value: "before",
      labelKey: "conditions.operators.beforeDate",
      summaryLabelKey: "conditions.operators.beforeDateSummary"
    },
    {
      value: "on",
      labelKey: "conditions.operators.onDate",
      summaryLabelKey: "conditions.operators.onDateSummary"
    },
    {
      value: "after",
      labelKey: "conditions.operators.afterDate",
      summaryLabelKey: "conditions.operators.afterDateSummary"
    },
    { value: "any", labelKey: "conditions.operators.hasAnyValue" },
    { value: "empty", labelKey: "conditions.operators.isEmpty" }
  ],
  // Bucketing attributes (ADR 0020): a Random A/B holds exactly `A` or `B`,
  // so only equality means anything; a Random number is a number.
  [AttributeDataType2.RandomAB]: [
    { value: "is", labelKey: "conditions.operators.is" },
    { value: "not", labelKey: "conditions.operators.isNot" }
  ],
  [AttributeDataType2.RandomNumber]: NUMBER_OPERATORS
};
function operatorsFor(dataType) {
  var _a;
  if (dataType === void 0)
    return OPERATORS_BY_DATATYPE[AttributeDataType2.String];
  return (_a = OPERATORS_BY_DATATYPE[dataType]) != null ? _a : OPERATORS_BY_DATATYPE[AttributeDataType2.String];
}
var VALUELESS_OPERATORS = /* @__PURE__ */ new Set(["any", "empty", "true", "false"]);
var DATE_PICKER_OPERATORS = /* @__PURE__ */ new Set(["before", "on", "after"]);
function splitOperatorTemplate(label) {
  const idx = label.indexOf("...");
  if (idx < 0)
    return null;
  return { prefix: label.slice(0, idx), suffix: label.slice(idx + 3) };
}

// src/conditions/validate.ts
import {
  AttributeBizTypes as AttributeBizTypes3,
  AttributeDataType as AttributeDataType3,
  EventCountLogic as EventCountLogic2,
  EventTimeLogic as EventTimeLogic2
} from "@usertour/types";
var isElementSelected = (data) => {
  if (!data)
    return false;
  if (data.type === "auto")
    return Boolean(data.selectors);
  if (data.type === "manual")
    return Boolean(data.customSelector);
  return false;
};
var cleanList = (values) => (values != null ? values : []).map((v) => v.trim()).filter((v) => v !== "");
var isOperatorValidForType = (logic, dataType) => operatorsFor(dataType).some((o) => o.value === logic);
var isValueMissing = (value) => isNullish(value) || isEmptyString(value);
function validateUserAttr(data, attributes) {
  if (!(data == null ? void 0 : data.attrId))
    return { key: "conditions.errors.userAttr.selectAttribute" };
  const attribute = attributes == null ? void 0 : attributes.find((a) => a.id === data.attrId);
  if (!attribute)
    return { key: "conditions.errors.userAttr.selectAttribute" };
  if (!data.logic)
    return { key: "conditions.errors.userAttr.selectOperator" };
  if (!isOperatorValidForType(data.logic, attribute.dataType)) {
    return {
      key: "conditions.errors.userAttr.invalidOperator",
      values: {
        dataType: attribute.dataType,
        allowedLogic: operatorsFor(attribute.dataType).map((o) => o.value)
      }
    };
  }
  if (VALUELESS_OPERATORS.has(data.logic))
    return void 0;
  if (data.logic === "between") {
    if (isValueMissing(data.value) || isValueMissing(data.value2))
      return { key: "conditions.errors.userAttr.enterValue" };
    return void 0;
  }
  if (attribute.dataType === AttributeDataType3.List) {
    if (!data.listValues || data.listValues.length === 0) {
      return { key: "conditions.errors.userAttr.enterValue" };
    }
    return void 0;
  }
  if (isValueMissing(data.value))
    return { key: "conditions.errors.userAttr.enterValue" };
  return void 0;
}
function validateCurrentPage(data) {
  if (cleanList(data == null ? void 0 : data.includes).length === 0 && cleanList(data == null ? void 0 : data.excludes).length === 0) {
    return { key: "conditions.errors.currentPage.enterPattern" };
  }
  return void 0;
}
function validateSegment(data, segments) {
  if (!(data == null ? void 0 : data.segmentId))
    return { key: "conditions.errors.segment.selectSegment" };
  if (segments && !segments.find((s) => s.id === data.segmentId)) {
    return { key: "conditions.errors.segment.selectSegment" };
  }
  return void 0;
}
function validateContent(data, contents) {
  if (!(data == null ? void 0 : data.contentId))
    return { key: "conditions.errors.content.selectContent" };
  if (contents && !contents.find((c) => c.id === data.contentId)) {
    return { key: "conditions.errors.content.selectContent" };
  }
  return void 0;
}
function validateElement(data) {
  if (!isElementSelected(data == null ? void 0 : data.elementData)) {
    return { key: "conditions.errors.element.selectElement" };
  }
  return void 0;
}
function validateTextInput(data) {
  var _a;
  if (!isElementSelected(data == null ? void 0 : data.elementData)) {
    return { key: "conditions.errors.element.selectElement" };
  }
  const logic = (_a = data == null ? void 0 : data.logic) != null ? _a : "";
  if (!["any", "empty"].includes(logic) && isValueMissing(data == null ? void 0 : data.value)) {
    return { key: "conditions.errors.userAttr.enterValue" };
  }
  return void 0;
}
function validateTextFill(data) {
  return validateElement(data);
}
function validateTime(data) {
  if (!data)
    return { key: "conditions.errors.time.enterStart" };
  const v2 = data;
  if (v2.startTime)
    return void 0;
  const legacy = data;
  if (legacy.startDate)
    return void 0;
  return { key: "conditions.errors.time.enterStart" };
}
var isMissing = isNullish;
function validateEvent(data, events) {
  if (!(data == null ? void 0 : data.eventId))
    return { key: "conditions.errors.event.selectEvent" };
  if (events && !events.find((e) => e.id === data.eventId)) {
    return { key: "conditions.errors.event.selectEvent" };
  }
  if (isMissing(data.count))
    return { key: "conditions.errors.event.enterCount" };
  if (data.countLogic === EventCountLogic2.BETWEEN && isMissing(data.count2)) {
    return { key: "conditions.errors.event.enterSecondCount" };
  }
  if (data.timeLogic !== EventTimeLogic2.AT_ANY_POINT_IN_TIME && isMissing(data.windowValue)) {
    return { key: "conditions.errors.event.enterTimeWindow" };
  }
  if (data.timeLogic === EventTimeLogic2.BETWEEN && isMissing(data.windowValue2)) {
    return { key: "conditions.errors.event.enterSecondTimeWindow" };
  }
  if (data.timeLogic !== EventTimeLogic2.AT_ANY_POINT_IN_TIME && isMissing(data.timeUnit)) {
    return { key: "conditions.errors.event.selectTimeUnit" };
  }
  return void 0;
}
function validateEventAttr(data, attributes) {
  if (!(data == null ? void 0 : data.attrId))
    return { key: "conditions.errors.eventAttr.selectAttribute" };
  const attribute = attributes == null ? void 0 : attributes.find(
    (a) => a.id === data.attrId && a.bizType === AttributeBizTypes3.Event
  );
  if (!attribute)
    return { key: "conditions.errors.eventAttr.selectAttribute" };
  if (!data.logic)
    return { key: "conditions.errors.userAttr.selectOperator" };
  if (!isOperatorValidForType(data.logic, attribute.dataType)) {
    return {
      key: "conditions.errors.userAttr.invalidOperator",
      values: {
        dataType: attribute.dataType,
        allowedLogic: operatorsFor(attribute.dataType).map((o) => o.value)
      }
    };
  }
  if (VALUELESS_OPERATORS.has(data.logic))
    return void 0;
  if (data.logic === "between") {
    if (isValueMissing(data.value) || isValueMissing(data.value2))
      return { key: "conditions.errors.userAttr.enterValue" };
    return void 0;
  }
  if (isValueMissing(data.value) && (!data.listValues || data.listValues.length === 0)) {
    return { key: "conditions.errors.userAttr.enterValue" };
  }
  return void 0;
}
function validateConditionByType(condition, ctx) {
  switch (condition.type) {
    case "user-attr":
      return validateUserAttr(condition.data, ctx.attributes);
    case "current-page":
      return validateCurrentPage(condition.data);
    case "segment":
      return validateSegment(condition.data, ctx.segments);
    case "content":
      return validateContent(condition.data, ctx.contents);
    case "element":
      return validateElement(condition.data);
    case "text-input":
      return validateTextInput(condition.data);
    case "text-fill":
      return validateTextFill(condition.data);
    case "time":
      return validateTime(condition.data);
    case "event":
      return validateEvent(condition.data, ctx.events);
    case "event-attr":
      return validateEventAttr(condition.data, ctx.attributes);
    default:
      return void 0;
  }
}

// src/attribute.ts
import { BizAttributeTypes as BizAttributeTypes3 } from "@usertour/types";
import { isValid as isValid2, parseISO as parseISO2 } from "date-fns";
var isValidISO8601 = (value) => {
  if (typeof value !== "string") {
    return false;
  }
  const iso8601Pattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
  if (!iso8601Pattern.test(value)) {
    return false;
  }
  try {
    const date = parseISO2(value);
    if (!isValid2(date)) {
      return false;
    }
    const isoString = date.toISOString();
    const normalizedInput = value.replace(/\.\d{3}Z$/, "Z");
    const normalizedIso = isoString.replace(/\.\d{3}Z$/, "Z");
    return normalizedInput === normalizedIso || value === isoString;
  } catch {
    return false;
  }
};
var getAttributeType = (attribute) => {
  const t = typeof attribute;
  if (t === "number") {
    return BizAttributeTypes3.Number;
  }
  if (t === "string") {
    if (isValidISO8601(attribute)) {
      return BizAttributeTypes3.DateTime;
    }
    return BizAttributeTypes3.String;
  }
  if (t === "boolean") {
    return BizAttributeTypes3.Boolean;
  }
  if (t === "object" && Array.isArray(attribute)) {
    return BizAttributeTypes3.List;
  }
  return BizAttributeTypes3.Nil;
};
var capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
var humanize = (name) => {
  return name.split(/[_\-.\s]+/).filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
};
var filterNullAttributes = (attributes) => {
  const attrs = {};
  for (const key in attributes) {
    const v = attributes[key];
    if (!isNull(v)) {
      attrs[key] = v;
    }
  }
  return attrs;
};

// src/attribute-write.ts
import {
  AttributeWriteOperation,
  BizAttributeTypes as BizAttributeTypes4
} from "@usertour/types";
import isEqual2 from "fast-deep-equal";
var OPERATION_KEYS = Object.values(AttributeWriteOperation);
var DATA_TYPE_KEY = "data_type";
var WRITE_DATA_TYPES = {
  string: BizAttributeTypes4.String,
  number: BizAttributeTypes4.Number,
  boolean: BizAttributeTypes4.Boolean,
  datetime: BizAttributeTypes4.DateTime,
  list: BizAttributeTypes4.List
};
var isScalar = (value) => {
  return isString(value) || isNumber(value) || isBoolean(value);
};
var toScalarList = (value) => {
  if (isScalar(value)) {
    return [value];
  }
  if (!isArray(value)) {
    return void 0;
  }
  const values = value.filter((item) => item !== null && item !== void 0);
  return values.every(isScalar) ? values : void 0;
};
var isPlainObject = (value) => {
  if (!isObject(value) || isArray(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};
var parseAttributeWrite = (value) => {
  if (isNull(value)) {
    return { ok: true, write: { kind: "delete" } };
  }
  if (!isObject(value) || isArray(value)) {
    return { ok: true, write: { kind: "literal", value } };
  }
  if (!isPlainObject(value)) {
    return { ok: false, reason: "an object value must be a plain operation object" };
  }
  const keys = Object.keys(value);
  const operations = keys.filter((key) => OPERATION_KEYS.includes(key));
  const extras = keys.filter((key) => !OPERATION_KEYS.includes(key) && key !== DATA_TYPE_KEY);
  if (operations.length !== 1 || extras.length > 0) {
    return {
      ok: false,
      reason: `an operation object must contain exactly one of ${OPERATION_KEYS.join(", ")}`
    };
  }
  const operation = operations[0];
  const operand = value[operation];
  const hasDataType = DATA_TYPE_KEY in value;
  if (hasDataType && operation !== AttributeWriteOperation.Set && operation !== AttributeWriteOperation.SetOnce) {
    return { ok: false, reason: "data_type is only allowed with set or set_once" };
  }
  let dataType;
  if (hasDataType) {
    const name = value[DATA_TYPE_KEY];
    if (!isString(name) || !Object.prototype.hasOwnProperty.call(WRITE_DATA_TYPES, name)) {
      return {
        ok: false,
        reason: `data_type must be one of ${Object.keys(WRITE_DATA_TYPES).join(", ")}`
      };
    }
    dataType = WRITE_DATA_TYPES[name];
  }
  switch (operation) {
    case AttributeWriteOperation.Set:
    case AttributeWriteOperation.SetOnce: {
      if (isNull(operand) || operand === void 0) {
        return { ok: false, reason: `${operation} requires a value` };
      }
      if (isObject(operand) && !isArray(operand)) {
        return { ok: false, reason: `${operation} value must be a literal or a list` };
      }
      return { ok: true, write: { kind: operation, value: operand, dataType } };
    }
    case AttributeWriteOperation.Add: {
      if (!isNumber(operand) || !Number.isFinite(operand)) {
        return { ok: false, reason: "add requires a finite number" };
      }
      return { ok: true, write: { kind: "add", value: operand } };
    }
    case AttributeWriteOperation.Union:
    case AttributeWriteOperation.Remove: {
      const values = toScalarList(operand);
      if (!values) {
        return { ok: false, reason: `${operation} requires a scalar or a list of scalars` };
      }
      return { ok: true, write: { kind: operation, values } };
    }
    default:
      return { ok: false, reason: "unknown operation" };
  }
};
var inferWriteDataType = (write) => {
  var _a;
  switch (write.kind) {
    case "add":
      return BizAttributeTypes4.Number;
    case "union":
    case "remove":
      return BizAttributeTypes4.List;
    case "set":
    case "set_once":
      return (_a = write.dataType) != null ? _a : getAttributeType(write.value);
    case "literal":
      return getAttributeType(write.value);
    default:
      return BizAttributeTypes4.Nil;
  }
};
var ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[Tt](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?:[Zz]|([+-])(\d{2}):?(\d{2}))$/;
var STRICT_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
var isLeapYear = (year) => {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
};
var daysInMonth = (year, month) => {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};
var normalizeIsoDateTime = (value) => {
  if (!isString(value)) {
    return void 0;
  }
  const match = ISO_DATE_TIME.exec(value);
  if (!match) {
    return void 0;
  }
  const [, y, mo, d, h, mi, s, fraction, sign, oh, om] = match;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(h);
  const minute = Number(mi);
  const second = Number(s);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return void 0;
  }
  if (hour > 23 || minute > 59 || second > 59) {
    return void 0;
  }
  const millis = fraction ? Number(`${fraction}00`.slice(0, 3)) : 0;
  let offsetMinutes = 0;
  if (sign) {
    const offsetHours = Number(oh);
    const offsetMins = Number(om);
    if (offsetHours > 14 || offsetMins > 59) {
      return void 0;
    }
    offsetMinutes = (sign === "-" ? -1 : 1) * (offsetHours * 60 + offsetMins);
  }
  if (STRICT_UTC.test(value)) {
    return value;
  }
  const date = /* @__PURE__ */ new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hour, minute, second, millis);
  const utc = new Date(date.getTime() - offsetMinutes * 6e4);
  const utcYear = utc.getUTCFullYear();
  if (utcYear < 0 || utcYear > 9999) {
    return void 0;
  }
  return utc.toISOString();
};
var coerceAttributeValue = (value, target) => {
  switch (target) {
    case BizAttributeTypes4.String:
      if (isString(value)) {
        return { ok: true, value };
      }
      if (isNumber(value) || isBoolean(value)) {
        return { ok: true, value: String(value) };
      }
      return { ok: false };
    case BizAttributeTypes4.Number:
      if (isNumber(value)) {
        return Number.isFinite(value) ? { ok: true, value } : { ok: false };
      }
      if (isString(value) && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) && String(parsed) === value ? { ok: true, value: parsed } : { ok: false };
      }
      return { ok: false };
    case BizAttributeTypes4.Boolean:
      if (isBoolean(value)) {
        return { ok: true, value };
      }
      if (value === "true" || value === "false") {
        return { ok: true, value: value === "true" };
      }
      return { ok: false };
    case BizAttributeTypes4.DateTime: {
      const normalized = normalizeIsoDateTime(value);
      return normalized ? { ok: true, value: normalized } : { ok: false };
    }
    case BizAttributeTypes4.List: {
      const values = toScalarList(value);
      return values ? { ok: true, value: values } : { ok: false };
    }
    default:
      return { ok: false };
  }
};
var ATTRIBUTE_DELETE = Symbol("attribute-delete");
var dedupe = (values) => {
  const result = [];
  for (const item of values) {
    if (!result.some((existing) => isEqual2(existing, item))) {
      result.push(item);
    }
  }
  return result;
};
var applyAttributeWrite = (current, write) => {
  switch (write.kind) {
    case "delete":
      return ATTRIBUTE_DELETE;
    case "literal":
    case "set":
      return write.value;
    case "set_once":
      return current === void 0 ? write.value : current;
    case "add":
      return (isNumber(current) ? current : 0) + write.value;
    case "union": {
      if (current === void 0) {
        return dedupe(write.values);
      }
      const existing = toScalarList(current);
      if (!existing) {
        return current;
      }
      return dedupe([...existing, ...write.values]);
    }
    case "remove": {
      if (current === void 0) {
        return ATTRIBUTE_DELETE;
      }
      const existing = toScalarList(current);
      if (!existing) {
        return current;
      }
      return existing.filter((item) => !write.values.some((removed) => isEqual2(removed, item)));
    }
    default:
      return current;
  }
};
var isAttributeOperation = (value) => {
  return isPlainObject(value);
};
var mergeAttributeCache = (cache, sent, refused = []) => {
  const dropped = new Set(refused);
  for (const codeName of Object.keys(sent)) {
    if (isAttributeOperation(sent[codeName])) {
      dropped.add(codeName);
    }
  }
  const next = {};
  for (const codeName of Object.keys(cache)) {
    if (!dropped.has(codeName)) {
      next[codeName] = cache[codeName];
    }
  }
  for (const codeName of Object.keys(sent)) {
    if (!dropped.has(codeName)) {
      next[codeName] = sent[codeName];
    }
  }
  return next;
};
var attributeDataEqual = (left, right) => {
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) {
    return false;
  }
  return leftKeys.every(
    (key) => Object.prototype.hasOwnProperty.call(right, key) && isEqual2(left[key], right[key])
  );
};
var attributeCacheChanged = (cache, incoming) => {
  if (Object.values(incoming).some(isAttributeOperation)) {
    return true;
  }
  return !attributeDataEqual(cache, { ...cache, ...incoming });
};
var normalizeLegacyAttributeWrites = (attributes, onRewrite) => {
  let result;
  for (const codeName of Object.keys(attributes)) {
    const value = attributes[codeName];
    if (!isPlainObject(value)) {
      continue;
    }
    const keys = Object.keys(value);
    if (keys.length !== 1) {
      continue;
    }
    const key = keys[0];
    let rewritten;
    if (key === "subtract" && isNumber(value.subtract)) {
      rewritten = { add: -value.subtract };
    } else if (key === "subtract" && isString(value.subtract) && value.subtract.trim() !== "") {
      const parsed = Number(value.subtract);
      rewritten = Number.isFinite(parsed) ? { add: -parsed } : void 0;
    } else if (key === "append" || key === "prepend") {
      rewritten = { union: value[key] };
    } else if (key === "add" && isString(value.add) && value.add.trim() !== "") {
      const parsed = Number(value.add);
      rewritten = Number.isFinite(parsed) ? { add: parsed } : void 0;
    }
    if (!rewritten) {
      continue;
    }
    result = result != null ? result : { ...attributes };
    result[codeName] = rewritten;
    const to = Object.keys(rewritten)[0];
    if (to !== key) {
      onRewrite == null ? void 0 : onRewrite({ codeName, from: key, to });
    }
  }
  return result != null ? result : attributes;
};

// src/content-helper.ts
import { DEFAULT_RESOURCE_CENTER_DATA } from "@usertour/constants";
import {
  ContentDataType as ContentDataType3,
  ContentEditorElementType as ContentEditorElementType3,
  LauncherIconSource,
  ResourceCenterBlockType
} from "@usertour/types";
var isRestrictedType = (type) => {
  const restrictedTypes = [
    ContentEditorElementType3.NPS,
    ContentEditorElementType3.STAR_RATING,
    ContentEditorElementType3.SCALE,
    ContentEditorElementType3.SINGLE_LINE_TEXT,
    ContentEditorElementType3.MULTI_LINE_TEXT,
    ContentEditorElementType3.MULTIPLE_CHOICE
  ];
  return restrictedTypes.includes(type);
};
var isMissingRequiredData = (element) => {
  var _a, _b, _c, _d;
  if (isRestrictedType(element.type)) {
    return isEmptyString((_a = element.data) == null ? void 0 : _a.name);
  }
  if (element.type === ContentEditorElementType3.BUTTON) {
    if (isEmptyString((_b = element.data) == null ? void 0 : _b.text)) {
      return true;
    }
    if (!((_c = element == null ? void 0 : element.data) == null ? void 0 : _c.actions) || ((_d = element == null ? void 0 : element.data) == null ? void 0 : _d.actions.length) === 0) {
      return true;
    }
  }
  if (element.type === ContentEditorElementType3.IMAGE || element.type === ContentEditorElementType3.EMBED) {
    return isEmptyString(element.url);
  }
  return false;
};
var hasMissingRequiredData = (contents) => {
  return contents.some(
    (group) => group.children.some(
      (column) => column.children.some((item) => isMissingRequiredData(item.element))
    )
  );
};
var isQuestionElement = (element) => {
  return element.type === ContentEditorElementType3.SINGLE_LINE_TEXT || element.type === ContentEditorElementType3.MULTI_LINE_TEXT || element.type === ContentEditorElementType3.NPS || element.type === ContentEditorElementType3.STAR_RATING || element.type === ContentEditorElementType3.SCALE || element.type === ContentEditorElementType3.MULTIPLE_CHOICE;
};
var isClickableElement = (element) => {
  return element.type === ContentEditorElementType3.BUTTON || isQuestionElement(element);
};
var extractQuestionData = (data) => {
  const result = [];
  function isQuestionRootElement(item) {
    return "element" in item && isQuestionElement(item.element);
  }
  function traverse(item) {
    if (isQuestionRootElement(item)) {
      result.push(item.element);
    }
    if ("children" in item && item.children) {
      for (const child of item.children) {
        traverse(child);
      }
    }
  }
  for (const item of data) {
    traverse(item);
  }
  return result;
};
var processQuestionElements = (contents) => {
  if (!contents || !isArray(contents) || contents.length === 0) {
    return [];
  }
  return contents.map((group) => ({
    ...group,
    children: group.children.map((column) => ({
      ...column,
      children: column.children.map((item) => {
        var _a, _b;
        const element = item.element;
        if (isQuestionElement(element)) {
          const questionElement = element;
          const updatedElement = {
            ...questionElement,
            data: {
              ...questionElement.data,
              cvid: cuid(),
              ...((_a = questionElement.data) == null ? void 0 : _a.actions) && isArray(questionElement.data.actions) ? { actions: regenerateConditionIds(questionElement.data.actions) } : {}
            }
          };
          return {
            ...item,
            element: updatedElement
          };
        }
        if (element.type === ContentEditorElementType3.BUTTON) {
          const buttonElement = element;
          if (((_b = buttonElement.data) == null ? void 0 : _b.actions) && isArray(buttonElement.data.actions)) {
            return {
              ...item,
              element: {
                ...buttonElement,
                data: {
                  ...buttonElement.data,
                  actions: regenerateConditionIds(buttonElement.data.actions)
                }
              }
            };
          }
        }
        return item;
      })
    }))
  }));
};
var generateUniqueCopyName = (originalName, existingNames) => {
  let name = `${originalName} (copy)`;
  if (existingNames == null ? void 0 : existingNames.includes(name)) {
    let number = 2;
    while (existingNames.includes(`${originalName} (copy ${number})`)) {
      number++;
    }
    name = `${originalName} (copy ${number})`;
  }
  return name;
};
var STEP_TYPE_LABELS = {
  tooltip: "Tooltip",
  modal: "Modal",
  bubble: "Bubble",
  hidden: "Hidden step"
};
var generateDefaultStepName = (type, existingNames) => {
  var _a;
  const label = (_a = STEP_TYPE_LABELS[type]) != null ? _a : "Step";
  const taken = new Set(existingNames != null ? existingNames : []);
  let number = 1;
  while (taken.has(`${label} ${number}`)) {
    number += 1;
  }
  return `${label} ${number}`;
};
var duplicateTriggers = (triggers) => {
  if (!isArray(triggers)) {
    return triggers;
  }
  return triggers.map((trigger) => ({
    ...trigger,
    id: cuid(),
    actions: isArray(trigger.actions) ? regenerateConditionIds(trigger.actions) : trigger.actions,
    conditions: isArray(trigger.conditions) ? regenerateConditionIds(trigger.conditions) : trigger.conditions
  }));
};
var duplicateTarget = (target) => {
  if (!target) {
    return void 0;
  }
  if (target.actions && isArray(target.actions)) {
    return {
      ...target,
      actions: regenerateConditionIds(target.actions)
    };
  }
  return target;
};
var duplicateChecklistData = (data) => {
  if (!data || !isObject(data) || !isArray(data.items)) {
    return data;
  }
  const checklistData = data;
  return {
    ...checklistData,
    content: processQuestionElements(checklistData.content),
    items: checklistData.items.map((item) => ({
      ...item,
      id: cuid(),
      clickedActions: isArray(item.clickedActions) ? regenerateConditionIds(item.clickedActions) : item.clickedActions,
      completeConditions: isArray(item.completeConditions) ? regenerateConditionIds(item.completeConditions) : item.completeConditions,
      onlyShowTaskConditions: isArray(item.onlyShowTaskConditions) ? regenerateConditionIds(item.onlyShowTaskConditions) : item.onlyShowTaskConditions
    }))
  };
};
var duplicateLauncherData = (data) => {
  if (!data || !isObject(data)) {
    return data;
  }
  const launcherData = data;
  return {
    ...launcherData,
    behavior: launcherData.behavior ? {
      ...launcherData.behavior,
      actions: isArray(launcherData.behavior.actions) ? regenerateConditionIds(launcherData.behavior.actions) : launcherData.behavior.actions
    } : launcherData.behavior,
    tooltip: launcherData.tooltip ? {
      ...launcherData.tooltip,
      content: processQuestionElements(launcherData.tooltip.content)
    } : launcherData.tooltip
  };
};
var duplicateConfig = (config) => {
  if (!config) {
    return config;
  }
  return {
    ...config,
    autoStartRules: config.autoStartRules ? regenerateConditionIds(config.autoStartRules) : config.autoStartRules,
    hideRules: config.hideRules ? regenerateConditionIds(config.hideRules) : config.hideRules
  };
};
var duplicateData = (data, contentType) => {
  if (contentType === ContentDataType3.CHECKLIST) {
    return duplicateChecklistData(data);
  }
  if (contentType === ContentDataType3.LAUNCHER) {
    return duplicateLauncherData(data);
  }
  return data;
};
var duplicateStep = (step) => {
  const { id, createdAt, updatedAt, versionId, trigger, target, data, ...rest } = step;
  return {
    ...rest,
    data: data ? processQuestionElements(data) : [],
    trigger: trigger ? duplicateTriggers(trigger) : [],
    target: duplicateTarget(target)
  };
};
var isRichTextEmpty = (nodes) => {
  if (!nodes || nodes.length === 0)
    return true;
  return nodes.every((node) => {
    var _a;
    if ("text" in node)
      return String((_a = node.text) != null ? _a : "").trim() === "";
    if ("children" in node)
      return isRichTextEmpty(node.children);
    return true;
  });
};
var isDisplayOnlyBlockType = (type) => type === ResourceCenterBlockType.RICH_TEXT || type === ResourceCenterBlockType.DIVIDER;
var createDefaultResourceCenterData = () => ({
  ...DEFAULT_RESOURCE_CENTER_DATA,
  tabs: [
    {
      id: cuid(),
      name: "Home",
      iconSource: LauncherIconSource.BUILTIN,
      iconType: "home-line",
      blocks: []
    }
  ]
});

// src/plan-features.ts
import { PLAN_FEATURES } from "@usertour/constants";
import { PlanType } from "@usertour/types";
function parseOverridePlan(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const input = raw;
  const out = {};
  for (const key of [
    "removeBranding",
    "customCss",
    "auditLogs",
    "ssoSaml",
    "ssoOidc",
    "webhooks"
  ]) {
    if (typeof input[key] === "boolean") {
      out[key] = input[key];
    }
  }
  for (const key of [
    "sessionsLimit",
    "teamMemberLimit",
    "environmentLimit",
    "dataRetentionYears",
    // Must be overridable alongside `auditLogs`: an override that grants the
    // boolean on a Hobby/Starter base otherwise keeps retention 0 — "unlocked
    // but forever empty".
    "auditLogRetentionDays"
  ]) {
    const value = input[key];
    if (typeof value === "number" || value === "unlimited") {
      out[key] = value;
    }
  }
  if (typeof input.apiRateLimit === "number") {
    out.apiRateLimit = input.apiRateLimit;
  }
  return out;
}
function resolvePlanFeatures(planType, overridePlan) {
  var _a;
  const base = (_a = PLAN_FEATURES[planType]) != null ? _a : PLAN_FEATURES[PlanType.HOBBY];
  const overrides = parseOverridePlan(overridePlan);
  return { ...base, ...overrides };
}
function isWithinLimit(limit, current) {
  return limit === "unlimited" || current < limit;
}

// src/token-scopes.ts
import { Capability } from "@usertour/types";
var ENV_TARGETED_CAPABILITIES = /* @__PURE__ */ new Set([
  Capability.ContentPublish,
  Capability.UserRead,
  Capability.UserWrite,
  Capability.UserDelete,
  Capability.CompanyRead,
  Capability.CompanyWrite,
  Capability.CompanyDelete,
  Capability.SessionRead,
  Capability.SessionManage,
  Capability.SegmentRead,
  Capability.SegmentCreate,
  Capability.SegmentUpdate,
  Capability.SegmentDelete,
  Capability.AnalyticsRead,
  Capability.WebhookRead,
  Capability.WebhookManage
]);
var requiresEnvironmentScope = (scopes) => scopes.some((s) => ENV_TARGETED_CAPABILITIES.has(s));
var environmentSelectionMissing = (scopes, environmentIds) => requiresEnvironmentScope(scopes) && !(environmentIds == null ? void 0 : environmentIds.length);

// src/localization.ts
import {
  ContentActionsItemType as ContentActionsItemType2,
  ContentDataType as ContentDataType4,
  ContentEditorElementType as ContentEditorElementType4,
  UserAttributes
} from "@usertour/types";
var isTranslationUnitOptional = (kind) => {
  return kind !== "text";
};
var TRANSLATION_UNIT_FIELDS = [
  // Rich text
  "text",
  "fallback",
  "link.url",
  // Elements
  "button.text",
  "navigate.url",
  "image.url",
  "image.alt",
  "image.link.url",
  "embed.url",
  "question.name",
  "question.lowLabel",
  "question.highLabel",
  "question.placeholder",
  "question.buttonText",
  "question.otherPlaceholder",
  "question.option",
  // Version data
  "buttonText",
  "headerText",
  "title",
  "readMoreLabel",
  "tab.name",
  "block.name",
  "item.name",
  "item.description",
  "contentItem.label"
];
var toText = (value) => {
  return typeof value === "string" ? value : "";
};
var isTranslatableText = (sourceText) => {
  return sourceText.trim() !== "";
};
var toPartnerText = (value) => {
  return typeof value === "string" ? value : void 0;
};
var alignChildren = (children, expectedLength) => {
  return isArray(children) && children.length === expectedLength ? children : void 0;
};
var linkDestination = (link) => {
  return { container: link, templateKey: "data", urlKey: "url" };
};
var navigateDestination = (actionData) => {
  return { container: actionData, templateKey: "value" };
};
var contentListDestination = (contentItem) => {
  return { container: contentItem, templateKey: "navigateUrl" };
};
var readDestination = (store) => {
  const template = store.container[store.templateKey];
  if (isArray(template)) {
    return serializeLinkTemplate(template, () => void 0);
  }
  return store.urlKey ? toText(store.container[store.urlKey]) : "";
};
var readDestinationForValidation = (store) => {
  var _a;
  const template = store.container[store.templateKey];
  if (isArray(template)) {
    return (_a = serializeLinkTemplate(template, () => "attribute")) != null ? _a : "";
  }
  return store.urlKey ? toText(store.container[store.urlKey]) : "";
};
var writeDestination = (store, value) => {
  const url = value.trim();
  store.container[store.templateKey] = [{ type: "paragraph", children: [{ text: url }] }];
  if (store.urlKey) {
    store.container[store.urlKey] = url;
  }
};
var inheritStoredDestination = (store, partnerStore) => {
  if (!partnerStore) {
    return;
  }
  const template = partnerStore.container[partnerStore.templateKey];
  const url = partnerStore.urlKey ? partnerStore.container[partnerStore.urlKey] : void 0;
  if (template === void 0 && url === void 0) {
    return;
  }
  store.container[store.templateKey] = isArray(template) ? deepClone(template) : template;
  if (store.urlKey) {
    store.container[store.urlKey] = url;
  }
};
var SAFE_DESTINATION_SCHEMES = /* @__PURE__ */ new Set(["http:", "https:", "mailto:", "tel:"]);
var RELATIVE_DESTINATION_BASE = "https://relative.invalid/";
var isSafeDestinationUrl = (value) => {
  const url = value.trim();
  if (url === "") {
    return true;
  }
  try {
    return SAFE_DESTINATION_SCHEMES.has(new URL(url, RELATIVE_DESTINATION_BASE).protocol);
  } catch {
    return false;
  }
};
var visitDestination = (store, partnerStore, path, field, visitor, opts) => {
  var _a;
  const sourceUrl = readDestination(store);
  if (sourceUrl || sourceUrl === "" && opts.includeEmptyLinkUnits) {
    visitor({
      path,
      sourceText: sourceUrl,
      partnerText: partnerStore ? readDestination(partnerStore) : void 0,
      kind: "destination",
      field,
      assign: (value) => writeDestination(store, value)
    });
    return;
  }
  if (sourceUrl === void 0 && opts.dynamicDestinationsAsPlaceholders) {
    visitor({
      path,
      sourceText: readDestinationForValidation(store),
      partnerText: void 0,
      kind: "destination",
      field,
      assign: () => void 0
    });
    return;
  }
  (_a = opts.onOpaqueDestination) == null ? void 0 : _a.call(opts, store, partnerStore);
};
var isNavigateAction = (action) => {
  const candidate = action;
  return (candidate == null ? void 0 : candidate.type) === ContentActionsItemType2.PAGE_NAVIGATE && Boolean(candidate.data) && typeof candidate.data === "object";
};
var navigateActionsOf = (actions) => {
  return isArray(actions) ? actions.filter(isNavigateAction) : [];
};
var walkActions = (actions, partnerActions, pathPrefix, visitor, opts) => {
  const partnerNavigates = navigateActionsOf(partnerActions);
  navigateActionsOf(actions).forEach((action, index) => {
    const partnerAction = partnerNavigates[index];
    visitDestination(
      navigateDestination(action.data),
      partnerAction ? navigateDestination(partnerAction.data) : void 0,
      `${pathPrefix}.${index}:navigate.url`,
      "navigate.url",
      visitor,
      opts
    );
  });
};
var walkSlateNodes = (nodes, partnerNodes, path, visitor, opts = {}, leafField = "text") => {
  const aligned = isArray(partnerNodes) && partnerNodes.length === nodes.length;
  nodes.forEach((node, index) => {
    if (!node || typeof node !== "object") {
      return;
    }
    const partnerNode = aligned ? partnerNodes[index] : void 0;
    const nodePath = `${path}.${index}`;
    if (typeof node.text === "string") {
      visitor({
        path: nodePath,
        sourceText: node.text,
        partnerText: toPartnerText(partnerNode == null ? void 0 : partnerNode.text),
        kind: "text",
        field: leafField,
        assign: (value) => {
          node.text = value;
        }
      });
      return;
    }
    if (isArray(node.children)) {
      const partnerChildren = partnerNode && partnerNode.type === node.type && isArray(partnerNode.children) ? partnerNode.children : void 0;
      walkSlateNodes(
        node.children,
        partnerChildren,
        nodePath,
        visitor,
        opts,
        leafField
      );
    }
    if (node.type === "user-attribute") {
      const partnerChip = (partnerNode == null ? void 0 : partnerNode.type) === "user-attribute" ? partnerNode : void 0;
      visitor({
        path: `${nodePath}:fallback`,
        sourceText: toText(node.fallback),
        partnerText: toPartnerText(partnerChip == null ? void 0 : partnerChip.fallback),
        kind: "text",
        field: "fallback",
        fieldArgs: { attribute: toText(node.attrCode) },
        assign: (value) => {
          node.fallback = value;
        }
      });
    }
    if (node.type === "link" && !opts.omitLinkUnits) {
      const partnerLink = (partnerNode == null ? void 0 : partnerNode.type) === "link" ? partnerNode : void 0;
      visitDestination(
        linkDestination(node),
        partnerLink ? linkDestination(partnerLink) : void 0,
        `${nodePath}:link.url`,
        "link.url",
        visitor,
        opts
      );
    }
  });
};
var LOCALIZED_LINKS_SCHEMA_VERSION = 1;
var LOCALIZED_DESTINATIONS_AND_ATTRIBUTES_SCHEMA_VERSION = 2;
var LOCALIZED_UNITS_SCHEMA_VERSION = LOCALIZED_DESTINATIONS_AND_ATTRIBUTES_SCHEMA_VERSION;
var blankStoredDestination = (store) => {
  const mirrorBlank = store.urlKey === void 0 || store.container[store.urlKey] === "";
  if (mirrorBlank && readDestination(store) === "") {
    return false;
  }
  writeDestination(store, "");
  return true;
};
var blankStoredText = (record, key) => {
  if (typeof record[key] !== "string" || record[key] === "") {
    return false;
  }
  record[key] = "";
  return true;
};
var blankStoresIntroducedIn = (version, record) => {
  switch (version) {
    case LOCALIZED_LINKS_SCHEMA_VERSION: {
      if (record.type === "link" && isArray(record.children)) {
        return blankStoredDestination(linkDestination(record));
      }
      if (record.type === ContentEditorElementType4.IMAGE && record.link && typeof record.link === "object") {
        return blankStoredDestination(linkDestination(record.link));
      }
      return false;
    }
    case LOCALIZED_DESTINATIONS_AND_ATTRIBUTES_SCHEMA_VERSION: {
      if (isNavigateAction(record)) {
        return blankStoredDestination(navigateDestination(record.data));
      }
      if (typeof record.contentId === "string" && isArray(record.navigateUrl)) {
        return blankStoredDestination(contentListDestination(record));
      }
      if (record.type === ContentEditorElementType4.IMAGE) {
        return blankStoredText(record, "alt");
      }
      if (record.type === "user-attribute") {
        return blankStoredText(record, "fallback");
      }
      return false;
    }
    default:
      return false;
  }
};
var blankLocalizedUnitClones = (value, storedSchemaVersion) => {
  let changed = false;
  const visit = (node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (isArray(node)) {
      for (const item of node) {
        visit(item);
      }
      return;
    }
    const record = node;
    for (let version = storedSchemaVersion + 1; version <= LOCALIZED_UNITS_SCHEMA_VERSION; version += 1) {
      changed = blankStoresIntroducedIn(version, record) || changed;
    }
    for (const child of Object.values(record)) {
      visit(child);
    }
  };
  visit(value);
  return changed;
};
var walkElementFields = (element, partnerElement, elementPath, outerVisitor, opts = {}) => {
  var _a, _b;
  const visitor = (visit) => {
    outerVisitor({ ...visit, element: { path: elementPath, type: element.type } });
  };
  const visitField = (field, sourceValue, partnerValue, kind, assign) => {
    visitor({
      path: `${elementPath}:${field}`,
      sourceText: toText(sourceValue),
      partnerText: toPartnerText(partnerValue),
      kind,
      field,
      assign
    });
  };
  const visitActions = (fieldPath, actions, partnerActions) => {
    walkActions(actions, partnerActions, `${elementPath}:${fieldPath}`, visitor, opts);
  };
  switch (element.type) {
    case ContentEditorElementType4.TEXT: {
      if (!isArray(element.data)) {
        return;
      }
      const partnerTextElement = partnerElement;
      walkSlateNodes(
        element.data,
        isArray(partnerTextElement == null ? void 0 : partnerTextElement.data) ? partnerTextElement == null ? void 0 : partnerTextElement.data : void 0,
        `${elementPath}:text`,
        visitor,
        opts
      );
      return;
    }
    case ContentEditorElementType4.BUTTON: {
      const buttonData = element.data;
      if (!buttonData) {
        return;
      }
      const partnerButton = partnerElement;
      visitField("button.text", buttonData.text, (_a = partnerButton == null ? void 0 : partnerButton.data) == null ? void 0 : _a.text, "text", (value) => {
        buttonData.text = value;
      });
      visitActions("button.actions", buttonData.actions, (_b = partnerButton == null ? void 0 : partnerButton.data) == null ? void 0 : _b.actions);
      return;
    }
    case ContentEditorElementType4.IMAGE: {
      const imageElement = element;
      const partnerImage = partnerElement;
      visitField("image.url", imageElement.url, partnerImage == null ? void 0 : partnerImage.url, "media", (value) => {
        imageElement.url = value;
      });
      visitField("image.alt", imageElement.alt, partnerImage == null ? void 0 : partnerImage.alt, "text", (value) => {
        imageElement.alt = value;
      });
      const imageLink = imageElement.link;
      if (imageLink && typeof imageLink === "object") {
        const partnerLink = (partnerImage == null ? void 0 : partnerImage.link) && typeof partnerImage.link === "object" ? partnerImage.link : void 0;
        visitDestination(
          linkDestination(imageLink),
          partnerLink ? linkDestination(partnerLink) : void 0,
          `${elementPath}:image.link.url`,
          "image.link.url",
          visitor,
          opts
        );
      }
      return;
    }
    case ContentEditorElementType4.EMBED: {
      const embedElement = element;
      const partnerEmbed = partnerElement;
      const sourceUrl = toText(embedElement.url);
      visitor({
        path: `${elementPath}:embed.url`,
        sourceText: sourceUrl,
        partnerText: toPartnerText(partnerEmbed == null ? void 0 : partnerEmbed.url),
        kind: "media",
        field: "embed.url",
        assign: (value) => {
          if (value !== embedElement.url) {
            embedElement.parsedUrl = void 0;
            embedElement.oembed = void 0;
          }
          embedElement.url = value;
        },
        applyEmbedResolution: (resolution) => {
          embedElement.parsedUrl = resolution.parsedUrl;
          embedElement.oembed = resolution.oembed;
        }
      });
      if (partnerEmbed && embedElement.url !== sourceUrl && embedElement.url === partnerEmbed.url) {
        embedElement.parsedUrl = partnerEmbed.parsedUrl;
        embedElement.oembed = partnerEmbed.oembed;
      }
      return;
    }
    case ContentEditorElementType4.NPS:
    case ContentEditorElementType4.STAR_RATING:
    case ContentEditorElementType4.SCALE: {
      const data = element.data;
      if (!data) {
        return;
      }
      const partnerData = partnerElement == null ? void 0 : partnerElement.data;
      visitField("question.name", data.name, partnerData == null ? void 0 : partnerData.name, "text", (value) => {
        data.name = value;
      });
      visitField("question.lowLabel", data.lowLabel, partnerData == null ? void 0 : partnerData.lowLabel, "text", (value) => {
        data.lowLabel = value;
      });
      visitField("question.highLabel", data.highLabel, partnerData == null ? void 0 : partnerData.highLabel, "text", (value) => {
        data.highLabel = value;
      });
      visitActions("question.actions", data.actions, partnerData == null ? void 0 : partnerData.actions);
      return;
    }
    case ContentEditorElementType4.SINGLE_LINE_TEXT:
    case ContentEditorElementType4.MULTI_LINE_TEXT: {
      const data = element.data;
      if (!data) {
        return;
      }
      const partnerData = partnerElement == null ? void 0 : partnerElement.data;
      visitField("question.name", data.name, partnerData == null ? void 0 : partnerData.name, "text", (value) => {
        data.name = value;
      });
      visitField(
        "question.placeholder",
        data.placeholder,
        partnerData == null ? void 0 : partnerData.placeholder,
        "text",
        (value) => {
          data.placeholder = value;
        }
      );
      visitField(
        "question.buttonText",
        data.buttonText,
        partnerData == null ? void 0 : partnerData.buttonText,
        "text",
        (value) => {
          data.buttonText = value;
        }
      );
      visitActions("question.actions", data.actions, partnerData == null ? void 0 : partnerData.actions);
      return;
    }
    case ContentEditorElementType4.MULTIPLE_CHOICE: {
      const data = element.data;
      if (!data) {
        return;
      }
      const partnerData = partnerElement == null ? void 0 : partnerElement.data;
      visitField("question.name", data.name, partnerData == null ? void 0 : partnerData.name, "text", (value) => {
        data.name = value;
      });
      visitField(
        "question.buttonText",
        data.buttonText,
        partnerData == null ? void 0 : partnerData.buttonText,
        "text",
        (value) => {
          data.buttonText = value;
        }
      );
      visitField(
        "question.otherPlaceholder",
        data.otherPlaceholder,
        partnerData == null ? void 0 : partnerData.otherPlaceholder,
        "text",
        (value) => {
          data.otherPlaceholder = value;
        }
      );
      const sourceOptions = isArray(data.options) ? data.options : [];
      const partnerOptions = alignChildren(partnerData == null ? void 0 : partnerData.options, sourceOptions.length);
      sourceOptions.forEach((option, optionIndex) => {
        var _a2;
        if (!option) {
          return;
        }
        visitor({
          path: `${elementPath}:question.options.${optionIndex}.label`,
          sourceText: toText(option.label),
          partnerText: toPartnerText((_a2 = partnerOptions == null ? void 0 : partnerOptions[optionIndex]) == null ? void 0 : _a2.label),
          kind: "text",
          field: "question.option",
          fieldArgs: { index: String(optionIndex + 1) },
          assign: (value) => {
            option.label = value;
          }
        });
      });
      visitActions("question.actions", data.actions, partnerData == null ? void 0 : partnerData.actions);
      return;
    }
    default:
      return;
  }
};
var walkTranslatableFields = (contents, partner, visitor, opts = {}) => {
  const partnerGroups = alignChildren(partner, contents.length);
  contents.forEach((group, groupIndex) => {
    if (!isArray(group == null ? void 0 : group.children)) {
      return;
    }
    const partnerGroup = partnerGroups == null ? void 0 : partnerGroups[groupIndex];
    const partnerColumns = alignChildren(partnerGroup == null ? void 0 : partnerGroup.children, group.children.length);
    group.children.forEach((column, columnIndex) => {
      if (!isArray(column == null ? void 0 : column.children)) {
        return;
      }
      const partnerColumn = partnerColumns == null ? void 0 : partnerColumns[columnIndex];
      const partnerItems = alignChildren(partnerColumn == null ? void 0 : partnerColumn.children, column.children.length);
      column.children.forEach((item, elementIndex) => {
        var _a, _b, _c;
        const element = item == null ? void 0 : item.element;
        if (!element) {
          return;
        }
        let partnerElement = (_a = partnerItems == null ? void 0 : partnerItems[elementIndex]) == null ? void 0 : _a.element;
        if ((partnerElement == null ? void 0 : partnerElement.type) !== element.type) {
          partnerElement = void 0;
        }
        if (partnerElement && isQuestionElement(element)) {
          const sourceCvid = (_b = element.data) == null ? void 0 : _b.cvid;
          const partnerCvid = (_c = partnerElement.data) == null ? void 0 : _c.cvid;
          if (sourceCvid !== partnerCvid) {
            partnerElement = void 0;
          }
        }
        walkElementFields(
          element,
          partnerElement,
          formatElementPath(groupIndex, columnIndex, elementIndex),
          visitor,
          opts
        );
      });
    });
  });
};
var formatElementPath = (groupIndex, columnIndex, elementIndex) => {
  return `${groupIndex}.${columnIndex}.${elementIndex}`;
};
var resolveTranslatedText = (visit) => {
  const value = visit.partnerText;
  return value === void 0 || value === "" ? void 0 : value;
};
var createApplyVisitor = (fallback) => {
  return (visit) => {
    var _a;
    const value = (_a = resolveTranslatedText(visit)) != null ? _a : fallback === "source" ? visit.sourceText : "";
    if (value !== visit.sourceText) {
      visit.assign(value);
    }
  };
};
var applyWalkOptions = (fallback) => {
  return fallback === "empty" ? { onOpaqueDestination: inheritStoredDestination } : {};
};
var createMissingCountVisitor = (count) => {
  return (visit) => {
    if (isTranslationUnitOptional(visit.kind) || !isTranslatableText(visit.sourceText)) {
      return;
    }
    if (visit.partnerText === void 0 || visit.partnerText === "") {
      count.missing += 1;
    }
  };
};
var createTranslatedPathCollector = (translated) => {
  return (visit) => {
    if (resolveTranslatedText(visit) !== void 0) {
      translated.add(visit.path);
    }
  };
};
var createOutdatedVisitor = (outdated, translated) => {
  return (visit) => {
    if (!isTranslatableText(visit.sourceText) || !translated.has(visit.path)) {
      return;
    }
    if (visit.partnerText !== visit.sourceText) {
      outdated.add(visit.path);
    }
  };
};
var applyLocalizedText = (source, localized, fallback) => {
  const clone = deepClone(source != null ? source : []);
  walkTranslatableFields(
    clone,
    localized,
    createApplyVisitor(fallback),
    applyWalkOptions(fallback)
  );
  return clone;
};
var mergeLocalizedEditorContents = (source, localized) => {
  return applyLocalizedText(source, localized, "source");
};
var createLocalizedWorkingContents = (source, localized) => {
  return applyLocalizedText(source, localized, "empty");
};
var extractTranslatableUnits = (contents) => {
  const units = [];
  walkTranslatableFields(contents != null ? contents : [], void 0, (visit) => {
    if (isTranslatableText(visit.sourceText)) {
      units.push({
        path: visit.path,
        text: visit.sourceText,
        kind: visit.kind,
        field: visit.field
      });
    }
  });
  return units;
};
var countMissingTranslations = (source, localized) => {
  const count = { missing: 0 };
  walkTranslatableFields(source != null ? source : [], localized, createMissingCountVisitor(count));
  return count.missing;
};
var collectOutdatedUnitPaths = (source, backup, localized) => {
  const translated = /* @__PURE__ */ new Set();
  walkTranslatableFields(source != null ? source : [], localized, createTranslatedPathCollector(translated));
  const outdated = /* @__PURE__ */ new Set();
  walkTranslatableFields(source != null ? source : [], backup, createOutdatedVisitor(outdated, translated));
  return outdated;
};
var withPathPrefix = (prefix, visitor) => {
  return (visit) => visitor({
    ...visit,
    path: `${prefix}/${visit.path}`,
    element: visit.element ? { ...visit.element, path: `${prefix}/${visit.element.path}` } : void 0
  });
};
var withGroup = (group, visitor) => {
  return (visit) => {
    var _a;
    return visitor({ ...visit, group: (_a = visit.group) != null ? _a : group });
  };
};
var richTextToPlainText = (nodes) => {
  if (!isArray(nodes)) {
    return "";
  }
  let text = "";
  for (const node of nodes) {
    if (!node || typeof node !== "object") {
      continue;
    }
    if (typeof node.text === "string") {
      text += node.text;
    } else if (isArray(node.children)) {
      text += richTextToPlainText(node.children);
    }
  }
  return text.trim();
};
var visitTextField = (visitor, path, field, sourceValue, partnerValue, assign) => {
  visitor({
    path,
    sourceText: toText(sourceValue),
    partnerText: toPartnerText(partnerValue),
    kind: "text",
    field,
    assign
  });
};
var walkEmbeddedContents = (prefix, contents, partnerContents, visitor, opts = {}) => {
  if (!isArray(contents)) {
    return;
  }
  walkTranslatableFields(
    contents,
    isArray(partnerContents) ? partnerContents : void 0,
    withPathPrefix(prefix, visitor),
    opts
  );
};
var walkChecklistFields = (data, partner, visitor, opts = {}) => {
  visitTextField(
    visitor,
    "buttonText",
    "buttonText",
    data.buttonText,
    partner == null ? void 0 : partner.buttonText,
    (value) => {
      data.buttonText = value;
    }
  );
  walkEmbeddedContents("content", data.content, partner == null ? void 0 : partner.content, visitor, opts);
  if (!isArray(data.items)) {
    return;
  }
  const partnerItems = isArray(partner == null ? void 0 : partner.items) ? partner.items : [];
  for (const item of data.items) {
    if (!(item == null ? void 0 : item.id)) {
      continue;
    }
    const partnerItem = partnerItems.find((candidate) => (candidate == null ? void 0 : candidate.id) === item.id);
    const itemPath = `items.${item.id}`;
    const itemVisitor = withGroup({ path: itemPath, title: toText(item.name) }, visitor);
    visitTextField(
      itemVisitor,
      `${itemPath}:name`,
      "item.name",
      item.name,
      partnerItem == null ? void 0 : partnerItem.name,
      (value) => {
        item.name = value;
      }
    );
    visitTextField(
      itemVisitor,
      `${itemPath}:description`,
      "item.description",
      item.description,
      partnerItem == null ? void 0 : partnerItem.description,
      (value) => {
        item.description = value;
      }
    );
    walkActions(
      item.clickedActions,
      partnerItem == null ? void 0 : partnerItem.clickedActions,
      `${itemPath}:clickedActions`,
      itemVisitor,
      opts
    );
  }
};
var walkLauncherFields = (data, partner, visitor, opts = {}) => {
  var _a, _b, _c, _d;
  visitTextField(
    visitor,
    "buttonText",
    "buttonText",
    data.buttonText,
    partner == null ? void 0 : partner.buttonText,
    (value) => {
      data.buttonText = value;
    }
  );
  walkActions(
    (_a = data.behavior) == null ? void 0 : _a.actions,
    (_b = partner == null ? void 0 : partner.behavior) == null ? void 0 : _b.actions,
    "behavior.actions",
    visitor,
    opts
  );
  walkEmbeddedContents("tooltip", (_c = data.tooltip) == null ? void 0 : _c.content, (_d = partner == null ? void 0 : partner.tooltip) == null ? void 0 : _d.content, visitor, opts);
};
var walkBannerFields = (data, partner, visitor, opts = {}) => {
  walkEmbeddedContents("contents", data.contents, partner == null ? void 0 : partner.contents, visitor, opts);
};
var walkAnnouncementFields = (data, partner, visitor, opts = {}) => {
  visitTextField(visitor, "title", "title", data.title, partner == null ? void 0 : partner.title, (value) => {
    data.title = value;
  });
  visitTextField(
    visitor,
    "readMoreLabel",
    "readMoreLabel",
    data.readMoreLabel,
    partner == null ? void 0 : partner.readMoreLabel,
    (value) => {
      data.readMoreLabel = value;
    }
  );
  walkEmbeddedContents("introContent", data.introContent, partner == null ? void 0 : partner.introContent, visitor, opts);
  walkEmbeddedContents("detailContent", data.detailContent, partner == null ? void 0 : partner.detailContent, visitor, opts);
};
var walkResourceCenterFields = (data, partner, visitor, opts = {}) => {
  visitTextField(
    visitor,
    "buttonText",
    "buttonText",
    data.buttonText,
    partner == null ? void 0 : partner.buttonText,
    (value) => {
      data.buttonText = value;
    }
  );
  visitTextField(
    visitor,
    "headerText",
    "headerText",
    data.headerText,
    partner == null ? void 0 : partner.headerText,
    (value) => {
      data.headerText = value;
    }
  );
  if (!isArray(data.tabs)) {
    return;
  }
  const partnerTabs = isArray(partner == null ? void 0 : partner.tabs) ? partner.tabs : [];
  const partnerBlocksById = new Map(
    partnerTabs.flatMap((tab) => isArray(tab == null ? void 0 : tab.blocks) ? tab.blocks : []).filter((block) => block == null ? void 0 : block.id).map((block) => [block.id, block])
  );
  for (const tab of data.tabs) {
    if (!(tab == null ? void 0 : tab.id)) {
      continue;
    }
    const partnerTab = partnerTabs.find((candidate) => (candidate == null ? void 0 : candidate.id) === tab.id);
    visitTextField(
      visitor,
      `tabs.${tab.id}:name`,
      "tab.name",
      tab.name,
      partnerTab == null ? void 0 : partnerTab.name,
      (value) => {
        tab.name = value;
      }
    );
    if (!isArray(tab.blocks)) {
      continue;
    }
    for (const block of tab.blocks) {
      if (!(block == null ? void 0 : block.id)) {
        continue;
      }
      const partnerBlock = partnerBlocksById.get(block.id);
      const blockPath = `tabs.${tab.id}.blocks.${block.id}`;
      const blockVisitor = withGroup(
        { path: blockPath, title: richTextToPlainText(block.name) },
        visitor
      );
      if (isArray(block.name)) {
        walkSlateNodes(
          block.name,
          isArray(partnerBlock == null ? void 0 : partnerBlock.name) ? partnerBlock.name : void 0,
          `${blockPath}:name`,
          blockVisitor,
          { ...opts, omitLinkUnits: true },
          "block.name"
        );
      }
      const blockContent = block.content;
      const partnerBlockContent = partnerBlock == null ? void 0 : partnerBlock.content;
      walkEmbeddedContents(
        `${blockPath}.content`,
        blockContent,
        partnerBlockContent,
        blockVisitor,
        opts
      );
      walkActions(
        block.clickedActions,
        partnerBlock == null ? void 0 : partnerBlock.clickedActions,
        `${blockPath}:clickedActions`,
        blockVisitor,
        opts
      );
      walkContentListItems(blockPath, block, partnerBlock, blockVisitor, opts);
    }
  }
};
var walkContentListItems = (blockPath, block, partnerBlock, visitor, opts) => {
  const contentItems = block.contentItems;
  if (!isArray(contentItems)) {
    return;
  }
  const partnerRaw = partnerBlock == null ? void 0 : partnerBlock.contentItems;
  const partnerItems = isArray(partnerRaw) ? partnerRaw : [];
  for (const contentItem of contentItems) {
    if (!(contentItem == null ? void 0 : contentItem.contentId)) {
      continue;
    }
    const partnerItem = partnerItems.find(
      (candidate) => (candidate == null ? void 0 : candidate.contentId) === contentItem.contentId
    );
    const itemPath = `${blockPath}.contentItems.${contentItem.contentId}`;
    visitTextField(
      visitor,
      `${itemPath}:label`,
      "contentItem.label",
      contentItem.label,
      partnerItem == null ? void 0 : partnerItem.label,
      (value) => {
        contentItem.label = value;
      }
    );
    visitDestination(
      contentListDestination(contentItem),
      partnerItem ? contentListDestination(partnerItem) : void 0,
      `${itemPath}:navigate.url`,
      "navigate.url",
      visitor,
      opts
    );
  }
};
var walkVersionDataFields = (contentType, data, partner, visitor, opts = {}) => {
  if (!data || typeof data !== "object") {
    return;
  }
  switch (contentType) {
    case ContentDataType4.CHECKLIST:
      walkChecklistFields(
        data,
        partner,
        visitor,
        opts
      );
      return;
    case ContentDataType4.LAUNCHER:
      walkLauncherFields(data, partner, visitor, opts);
      return;
    case ContentDataType4.BANNER:
      walkBannerFields(data, partner, visitor, opts);
      return;
    case ContentDataType4.ANNOUNCEMENT:
      walkAnnouncementFields(
        data,
        partner,
        visitor,
        opts
      );
      return;
    case ContentDataType4.RESOURCE_CENTER:
      walkResourceCenterFields(
        data,
        partner,
        visitor,
        opts
      );
      return;
    default:
      return;
  }
};
var isVersionDataLocalizable = (contentType) => {
  return contentType === ContentDataType4.CHECKLIST || contentType === ContentDataType4.LAUNCHER || contentType === ContentDataType4.BANNER || contentType === ContentDataType4.ANNOUNCEMENT || contentType === ContentDataType4.RESOURCE_CENTER;
};
var mergeLocalizedVersionData = (contentType, source, localized) => {
  const clone = deepClone(source);
  walkVersionDataFields(
    contentType,
    clone,
    localized,
    createApplyVisitor("source"),
    applyWalkOptions("source")
  );
  return clone;
};
var createLocalizedWorkingVersionData = (contentType, source, localized) => {
  const clone = deepClone(source);
  walkVersionDataFields(
    contentType,
    clone,
    localized,
    createApplyVisitor("empty"),
    applyWalkOptions("empty")
  );
  return clone;
};
var countMissingVersionDataTranslations = (contentType, source, localized) => {
  const count = { missing: 0 };
  walkVersionDataFields(contentType, source, localized, createMissingCountVisitor(count));
  return count.missing;
};
var collectOutdatedVersionDataPaths = (contentType, source, backup, localized) => {
  const translated = /* @__PURE__ */ new Set();
  walkVersionDataFields(contentType, source, localized, createTranslatedPathCollector(translated));
  const outdated = /* @__PURE__ */ new Set();
  walkVersionDataFields(contentType, source, backup, createOutdatedVisitor(outdated, translated));
  return outdated;
};
var isHttpUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};
var createTranslationUnitCollector = (units) => {
  return (visit) => {
    var _a;
    if (!isTranslatableText(visit.sourceText)) {
      return;
    }
    units.push({
      path: visit.path,
      sourceText: visit.sourceText,
      translatedText: (_a = resolveTranslatedText(visit)) != null ? _a : "",
      kind: visit.kind,
      field: visit.field,
      ...visit.fieldArgs ? { fieldArgs: visit.fieldArgs } : {},
      ...visit.element ? { element: visit.element } : {},
      ...visit.group ? { group: visit.group } : {}
    });
  };
};
var createTranslationApplier = (translations, embedResolutions) => {
  return (visit) => {
    const value = translations.get(visit.path);
    if (value === null) {
      visit.assign("");
      return;
    }
    if (typeof value === "string" && value.trim() !== "") {
      visit.assign(value);
      const resolution = embedResolutions == null ? void 0 : embedResolutions.get(value.trim());
      if (resolution && visit.applyEmbedResolution) {
        visit.applyEmbedResolution(resolution);
      }
    }
  };
};
var extractContentsTranslationUnits = (source, localized) => {
  const units = [];
  walkTranslatableFields(source != null ? source : [], localized, createTranslationUnitCollector(units));
  return units;
};
var createDestinationCollector = (destinations) => {
  return (visit) => {
    if (visit.kind === "destination" && visit.sourceText !== "") {
      destinations.push({ path: visit.path, value: visit.sourceText });
    }
  };
};
var collectContentsDestinations = (contents) => {
  const destinations = [];
  walkTranslatableFields(contents != null ? contents : [], void 0, createDestinationCollector(destinations), {
    dynamicDestinationsAsPlaceholders: true
  });
  return destinations;
};
var collectActionListDestinations = (actions, pathPrefix) => {
  const destinations = [];
  walkActions(actions, void 0, pathPrefix, createDestinationCollector(destinations), {
    dynamicDestinationsAsPlaceholders: true
  });
  return destinations;
};
var collectVersionDataDestinations = (contentType, data) => {
  const destinations = [];
  walkVersionDataFields(contentType, data, void 0, createDestinationCollector(destinations), {
    dynamicDestinationsAsPlaceholders: true
  });
  return destinations;
};
var extractVersionDataTranslationUnits = (contentType, source, localized) => {
  const units = [];
  walkVersionDataFields(contentType, source, localized, createTranslationUnitCollector(units));
  return units;
};
var applyContentsTranslationUnits = (source, localized, translations, embedResolutions) => {
  const working = applyLocalizedText(source, localized, "empty");
  walkTranslatableFields(
    working,
    void 0,
    createTranslationApplier(translations, embedResolutions),
    { includeEmptyLinkUnits: true }
  );
  return working;
};
var applyVersionDataTranslationUnits = (contentType, source, localized, translations, embedResolutions) => {
  const working = createLocalizedWorkingVersionData(contentType, source, localized);
  walkVersionDataFields(
    contentType,
    working,
    void 0,
    createTranslationApplier(translations, embedResolutions),
    { includeEmptyLinkUnits: true }
  );
  return working;
};
var createTranslationProbe = () => {
  const state = { found: false };
  const visitor = (visit) => {
    if (visit.sourceText !== "") {
      state.found = true;
    }
  };
  return { state, visitor };
};
var elementHasTranslations = (element) => {
  const probe = createTranslationProbe();
  walkElementFields(element, void 0, "", probe.visitor);
  return probe.state.found;
};
var columnHasTranslations = (column) => {
  if (!isArray(column == null ? void 0 : column.children)) {
    return false;
  }
  return column.children.some((item) => (item == null ? void 0 : item.element) && elementHasTranslations(item.element));
};
var groupHasTranslations = (group) => {
  if (!isArray(group == null ? void 0 : group.children)) {
    return false;
  }
  return group.children.some((column) => columnHasTranslations(column));
};
var contentsHaveTranslations = (contents) => {
  return contents.some((group) => groupHasTranslations(group));
};
var slateNodesHaveTranslations = (nodes) => {
  const probe = createTranslationProbe();
  walkSlateNodes(nodes, void 0, "", probe.visitor);
  return probe.state.found;
};
var graftSlateNodes = (working, stored) => {
  if (!isArray(stored)) {
    return working;
  }
  if (stored.length !== working.length) {
    return slateNodesHaveTranslations(working) ? working : deepClone(stored);
  }
  working.forEach((node, index) => {
    if (!node || typeof node !== "object" || !isArray(node.children)) {
      return;
    }
    const storedNode = stored[index];
    const storedChildren = storedNode && storedNode.type === node.type && isArray(storedNode.children) ? storedNode.children : void 0;
    node.children = graftSlateNodes(node.children, storedChildren);
  });
  return working;
};
var graftElementTranslations = (element, storedElement) => {
  if (element.type === ContentEditorElementType4.TEXT) {
    const workingData = element.data;
    const storedData = storedElement.data;
    if (isArray(workingData)) {
      element.data = graftSlateNodes(
        workingData,
        isArray(storedData) ? storedData : void 0
      );
    }
    return;
  }
  if (element.type === ContentEditorElementType4.MULTIPLE_CHOICE) {
    const data = element.data;
    const storedData = storedElement.data;
    const options = isArray(data == null ? void 0 : data.options) ? data.options : void 0;
    const storedOptions = storedData == null ? void 0 : storedData.options;
    if (!options || !isArray(storedOptions) || storedOptions.length === options.length) {
      return;
    }
    const optionsHaveTranslations = options.some((option) => toText(option == null ? void 0 : option.label) !== "");
    if (!optionsHaveTranslations) {
      data.options = deepClone(storedOptions);
    }
  }
};
var graftContentsTranslations = (working, stored) => {
  if (!isArray(stored) || stored.length === 0) {
    return working;
  }
  const storedGroups = alignChildren(stored, working.length);
  if (!storedGroups) {
    return contentsHaveTranslations(working) ? working : deepClone(stored);
  }
  working.forEach((group, groupIndex) => {
    if (!isArray(group == null ? void 0 : group.children)) {
      return;
    }
    const storedGroup = storedGroups[groupIndex];
    const storedColumns = alignChildren(storedGroup == null ? void 0 : storedGroup.children, group.children.length);
    if (!storedColumns) {
      if (isArray(storedGroup == null ? void 0 : storedGroup.children) && !groupHasTranslations(group)) {
        group.children = deepClone(storedGroup.children);
      }
      return;
    }
    group.children.forEach((column, columnIndex) => {
      if (!isArray(column == null ? void 0 : column.children)) {
        return;
      }
      const storedColumn = storedColumns[columnIndex];
      const storedItems = alignChildren(storedColumn == null ? void 0 : storedColumn.children, column.children.length);
      if (!storedItems) {
        if (isArray(storedColumn == null ? void 0 : storedColumn.children) && !columnHasTranslations(column)) {
          column.children = deepClone(storedColumn.children);
        }
        return;
      }
      column.children.forEach((item, elementIndex) => {
        var _a, _b, _c;
        const element = item == null ? void 0 : item.element;
        const storedElement = (_a = storedItems[elementIndex]) == null ? void 0 : _a.element;
        if (!element || !storedElement) {
          return;
        }
        let aligned = storedElement.type === element.type;
        if (aligned && isQuestionElement(element)) {
          aligned = ((_b = element.data) == null ? void 0 : _b.cvid) === ((_c = storedElement.data) == null ? void 0 : _c.cvid);
        }
        if (!aligned) {
          if (!elementHasTranslations(element)) {
            item.element = deepClone(storedElement);
          }
          return;
        }
        graftElementTranslations(element, storedElement);
      });
    });
  });
  return working;
};
var buildLocalizedFlowSavePayload = (working, stored) => {
  const payload = {};
  if (stored) {
    for (const [cvid, contents] of Object.entries(stored)) {
      if (!(cvid in working)) {
        payload[cvid] = deepClone(contents);
      }
    }
  }
  for (const [cvid, contents] of Object.entries(working)) {
    payload[cvid] = graftContentsTranslations(deepClone(contents), stored == null ? void 0 : stored[cvid]);
  }
  return payload;
};
var buildLocalizedFlowBackup = (steps, storedBackup) => {
  const current = Object.fromEntries(
    steps.map((step) => [step.cvid, step.data])
  );
  if (!storedBackup) {
    return current;
  }
  const preserved = Object.entries(storedBackup).filter(([cvid]) => !(cvid in current));
  return { ...Object.fromEntries(preserved), ...current };
};
var buildLocalizedVersionDataBackup = (sourceData) => {
  return sourceData != null ? sourceData : {};
};
var graftEmbeddedContents = (container, key, storedContents) => {
  const workingContents = container[key];
  if (isArray(workingContents)) {
    container[key] = graftContentsTranslations(
      workingContents,
      isArray(storedContents) ? storedContents : void 0
    );
    return;
  }
  if (isArray(storedContents)) {
    container[key] = deepClone(storedContents);
  }
};
var graftChecklistTranslations = (working, stored) => {
  graftEmbeddedContents(working, "content", stored.content);
  if (!isArray(working.items) || !isArray(stored.items)) {
    return;
  }
  const knownItemIds = new Set(working.items.map((item) => item == null ? void 0 : item.id).filter(Boolean));
  for (const storedItem of stored.items) {
    if ((storedItem == null ? void 0 : storedItem.id) && !knownItemIds.has(storedItem.id)) {
      working.items.push(deepClone(storedItem));
    }
  }
};
var graftLauncherTranslations = (working, stored) => {
  var _a;
  if (working.tooltip && typeof working.tooltip === "object") {
    graftEmbeddedContents(
      working.tooltip,
      "content",
      (_a = stored.tooltip) == null ? void 0 : _a.content
    );
  }
};
var graftAnnouncementTranslations = (working, stored) => {
  const container = working;
  graftEmbeddedContents(container, "introContent", stored.introContent);
  graftEmbeddedContents(container, "detailContent", stored.detailContent);
};
var graftContentListItemLabels = (block, storedBlock) => {
  const workingItems = block.contentItems;
  const storedRaw = storedBlock.contentItems;
  if (!isArray(workingItems) || !isArray(storedRaw)) {
    return;
  }
  const knownIds = new Set(
    workingItems.map((item) => item == null ? void 0 : item.contentId).filter(Boolean)
  );
  for (const storedItem of storedRaw) {
    if ((storedItem == null ? void 0 : storedItem.contentId) && !knownIds.has(storedItem.contentId)) {
      workingItems.push(deepClone(storedItem));
    }
  }
};
var graftResourceCenterTranslations = (working, stored) => {
  if (!isArray(working.tabs) || !isArray(stored.tabs)) {
    return;
  }
  const workingTabs = working.tabs;
  const storedTabs = stored.tabs;
  const workingTabIds = new Set(workingTabs.map((tab) => tab == null ? void 0 : tab.id).filter(Boolean));
  const workingBlockIds = new Set(
    workingTabs.flatMap((tab) => isArray(tab == null ? void 0 : tab.blocks) ? tab.blocks : []).map((block) => block == null ? void 0 : block.id).filter(Boolean)
  );
  const storedBlocksById = new Map(
    storedTabs.flatMap((tab) => isArray(tab == null ? void 0 : tab.blocks) ? tab.blocks : []).filter((block) => block == null ? void 0 : block.id).map((block) => [block.id, block])
  );
  for (const tab of workingTabs) {
    if (!isArray(tab == null ? void 0 : tab.blocks)) {
      continue;
    }
    for (const block of tab.blocks) {
      if (!(block == null ? void 0 : block.id)) {
        continue;
      }
      const storedBlock = storedBlocksById.get(block.id);
      if (!storedBlock) {
        continue;
      }
      if (isArray(block.name)) {
        block.name = graftSlateNodes(
          block.name,
          isArray(storedBlock.name) ? storedBlock.name : void 0
        );
      }
      graftEmbeddedContents(
        block,
        "content",
        storedBlock.content
      );
      graftContentListItemLabels(block, storedBlock);
    }
  }
  for (const storedTab of storedTabs) {
    if (!(storedTab == null ? void 0 : storedTab.id)) {
      continue;
    }
    if (!workingTabIds.has(storedTab.id)) {
      workingTabs.push(deepClone(storedTab));
      continue;
    }
    if (!isArray(storedTab.blocks)) {
      continue;
    }
    const targetTab = workingTabs.find((tab) => (tab == null ? void 0 : tab.id) === storedTab.id);
    if (!targetTab || !isArray(targetTab.blocks)) {
      continue;
    }
    for (const storedBlock of storedTab.blocks) {
      if ((storedBlock == null ? void 0 : storedBlock.id) && !workingBlockIds.has(storedBlock.id)) {
        targetTab.blocks.push(deepClone(storedBlock));
      }
    }
  }
};
var buildLocalizedVersionDataSavePayload = (contentType, working, stored) => {
  const payload = deepClone(working);
  if (!payload || typeof payload !== "object" || !stored || typeof stored !== "object") {
    return payload;
  }
  switch (contentType) {
    case ContentDataType4.CHECKLIST:
      graftChecklistTranslations(payload, stored);
      break;
    case ContentDataType4.LAUNCHER:
      graftLauncherTranslations(payload, stored);
      break;
    case ContentDataType4.BANNER:
      graftEmbeddedContents(
        payload,
        "contents",
        stored.contents
      );
      break;
    case ContentDataType4.ANNOUNCEMENT:
      graftAnnouncementTranslations(
        payload,
        stored
      );
      break;
    case ContentDataType4.RESOURCE_CENTER:
      graftResourceCenterTranslations(
        payload,
        stored
      );
      break;
    default:
      break;
  }
  return payload;
};
var remapContentsTranslationIdentifiers = (duplicated, localized) => {
  if (!isArray(duplicated) || !isArray(localized)) {
    return localized;
  }
  const result = deepClone(localized);
  const resultGroups = alignChildren(result, duplicated.length);
  if (!resultGroups) {
    return result;
  }
  duplicated.forEach((group, groupIndex) => {
    var _a;
    if (!isArray(group == null ? void 0 : group.children)) {
      return;
    }
    const resultColumns = alignChildren((_a = resultGroups[groupIndex]) == null ? void 0 : _a.children, group.children.length);
    if (!resultColumns) {
      return;
    }
    group.children.forEach((column, columnIndex) => {
      var _a2;
      if (!isArray(column == null ? void 0 : column.children)) {
        return;
      }
      const resultItems = alignChildren(
        (_a2 = resultColumns[columnIndex]) == null ? void 0 : _a2.children,
        column.children.length
      );
      if (!resultItems) {
        return;
      }
      column.children.forEach((item, elementIndex) => {
        var _a3, _b;
        const element = item == null ? void 0 : item.element;
        const resultElement = (_a3 = resultItems[elementIndex]) == null ? void 0 : _a3.element;
        if (!element || !resultElement || element.type !== resultElement.type) {
          return;
        }
        if (isQuestionElement(element)) {
          const questionData = resultElement.data;
          const duplicatedCvid = (_b = element.data) == null ? void 0 : _b.cvid;
          if (questionData && duplicatedCvid) {
            questionData.cvid = duplicatedCvid;
          }
        }
      });
    });
  });
  return result;
};
var remapFlowTranslationIdentifiers = (duplicatedSteps, localized) => {
  if (!localized || typeof localized !== "object") {
    return localized;
  }
  const result = deepClone(localized);
  for (const step of duplicatedSteps) {
    if (!step.cvid || !result[step.cvid]) {
      continue;
    }
    const remapped = remapContentsTranslationIdentifiers(
      isArray(step.data) ? step.data : void 0,
      result[step.cvid]
    );
    if (remapped) {
      result[step.cvid] = remapped;
    }
  }
  return result;
};
var remapVersionDataTranslationIdentifiers = (contentType, duplicatedData, localized) => {
  var _a;
  if (!localized || typeof localized !== "object" || !duplicatedData) {
    return localized;
  }
  if (contentType === ContentDataType4.CHECKLIST) {
    const duplicated = duplicatedData;
    const result = deepClone(localized);
    const remappedContent = remapContentsTranslationIdentifiers(
      isArray(duplicated.content) ? duplicated.content : void 0,
      isArray(result.content) ? result.content : void 0
    );
    if (remappedContent) {
      result.content = remappedContent;
    }
    if (isArray(duplicated.items) && isArray(result.items)) {
      const items = alignChildren(result.items, duplicated.items.length);
      if (items) {
        duplicated.items.forEach((item, index) => {
          if ((item == null ? void 0 : item.id) && items[index]) {
            items[index].id = item.id;
          }
        });
      }
    }
    return result;
  }
  if (contentType === ContentDataType4.LAUNCHER) {
    const duplicated = duplicatedData;
    const result = deepClone(localized);
    if (result.tooltip && typeof result.tooltip === "object") {
      const remappedContent = remapContentsTranslationIdentifiers(
        isArray((_a = duplicated.tooltip) == null ? void 0 : _a.content) ? duplicated.tooltip.content : void 0,
        isArray(result.tooltip.content) ? result.tooltip.content : void 0
      );
      if (remappedContent) {
        result.tooltip.content = remappedContent;
      }
    }
    return result;
  }
  return localized;
};
var resolveUserLocaleCode = (userAttributes) => {
  const attributes = userAttributes;
  const explicit = attributes == null ? void 0 : attributes[UserAttributes.LOCALE_CODE];
  if (typeof explicit === "string" && explicit.trim() !== "") {
    return explicit;
  }
  return null;
};
var matchTranslationByLocale = (translations, localeCode) => {
  const normalize = (code) => code.trim().toLowerCase();
  const normalized = normalize(localeCode);
  const exact = translations.find(
    (translation) => normalize(translation.localization.code) === normalized
  );
  if (exact) {
    return exact;
  }
  const primarySubtag = normalized.split("-")[0];
  return translations.find(
    (translation) => normalize(translation.localization.code).split("-")[0] === primarySubtag
  );
};

// src/connection-recovery.ts
var CLIENT_DISCONNECT_REASON = "io client disconnect";
var SERVER_DISCONNECT_REASON = "io server disconnect";
var reduceConnection = (state, signal) => {
  switch (signal.type) {
    case "connect_requested":
      if (state === "rejected" && signal.hadConnected === true) {
        return { state: "reconnecting", actions: [] };
      }
      if (state === "idle" || state === "rejected") {
        return { state: "connecting", actions: [] };
      }
      return { state, actions: [] };
    case "connect":
      if (state === "connected") {
        return { state, actions: [] };
      }
      return {
        state: "connected",
        actions: state === "reconnecting" ? ["replay", "evaluate"] : ["replay"]
      };
    case "disconnect":
      if (signal.reason === CLIENT_DISCONNECT_REASON) {
        return { state: "idle", actions: [] };
      }
      if (signal.reason === SERVER_DISCONNECT_REASON) {
        return { state: "reconnecting", actions: ["schedule_reconnect"] };
      }
      return { state: "reconnecting", actions: [] };
    case "connect_error": {
      if (!signal.retryable) {
        return { state: "rejected", actions: ["stop"] };
      }
      const next = state === "connecting" ? "connecting" : "reconnecting";
      return { state: next, actions: signal.active ? [] : ["schedule_reconnect"] };
    }
    default:
      return { state, actions: [] };
  }
};
var RECONNECT_BASE_DELAY_MS = 1e3;
var RECONNECT_MAX_DELAY_MS = 6e4;
var RECONNECT_JITTER = 0.25;
var reconnectDelayMs = (attempt, random = Math.random) => {
  const exponent = Math.max(0, Math.min(attempt, 30));
  const base = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** exponent, RECONNECT_MAX_DELAY_MS);
  const jitter = 1 - RECONNECT_JITTER + random() * RECONNECT_JITTER * 2;
  return Math.round(base * jitter);
};
var isRetryableHandshakeError = (error) => {
  if (!isObject(error)) {
    return true;
  }
  const data = error.data;
  if (!isObject(data)) {
    return true;
  }
  return data.retryable !== false;
};
var PENDING_WRITE_TTL_MS = 10 * 6e4;
var MAX_WRITE_REPLAYS = 1;
var mergeFailedWrite = (previous, next) => {
  if (!previous) {
    return next;
  }
  const merged = { ...previous, ...next };
  if (previous.attributes || next.attributes) {
    merged.attributes = { ...previous.attributes, ...next.attributes };
  }
  if (previous.membership || next.membership) {
    merged.membership = { ...previous.membership, ...next.membership };
  }
  return merged;
};
var recordFailedWrite = (previous, write, at) => {
  var _a;
  const merged = mergeFailedWrite(previous == null ? void 0 : previous.write, write);
  const stripped = withFields(
    merged,
    stripNonIdempotentWrites(merged.attributes),
    stripNonIdempotentWrites(merged.membership)
  );
  const stamps = keepStampsOf(
    {
      attributes: { ...previous == null ? void 0 : previous.stamps.attributes, ...stampAll(write.attributes, at) },
      membership: { ...previous == null ? void 0 : previous.stamps.membership, ...stampAll(write.membership, at) }
    },
    stripped
  );
  return { write: stripped, stamps, failedAt: at, replays: (_a = previous == null ? void 0 : previous.replays) != null ? _a : 0 };
};
var retireWrittenKeys = (record, written) => {
  const write = withoutWrittenKeys(record.write, written);
  if (!write) {
    return void 0;
  }
  return { ...record, write, stamps: keepStampsOf(record.stamps, write) };
};
var withoutExpiredKeys = (record, now, ttl) => {
  const fresh = (key, stamps) => {
    var _a;
    return now - ((_a = stamps[key]) != null ? _a : record.failedAt) <= ttl;
  };
  const attributes = pickKeys(
    record.write.attributes,
    (key) => fresh(key, record.stamps.attributes)
  );
  const membership = pickKeys(
    record.write.membership,
    (key) => fresh(key, record.stamps.membership)
  );
  if (isEmptyRecord(attributes) && isEmptyRecord(membership) && now - record.failedAt > ttl) {
    return void 0;
  }
  const write = withFields(record.write, attributes, membership);
  return { ...record, write, stamps: keepStampsOf(record.stamps, write) };
};
var noteSentKeys = (sent, write, seq) => ({
  attributes: { ...sent == null ? void 0 : sent.attributes, ...stampAll(write.attributes, seq) },
  membership: { ...sent == null ? void 0 : sent.membership, ...stampAll(write.membership, seq) }
});
var keysStillOwnedBy = (write, sent, seq) => {
  if (isEmptyRecord(write.attributes) && isEmptyRecord(write.membership)) {
    return write;
  }
  const attributes = pickKeys(write.attributes, (key) => sent.attributes[key] === seq);
  const membership = pickKeys(write.membership, (key) => sent.membership[key] === seq);
  if (isEmptyRecord(attributes) && isEmptyRecord(membership)) {
    return void 0;
  }
  return withFields(write, attributes, membership);
};
var withoutWrittenKeys = (failed, written) => {
  const attributes = omitKeys(failed.attributes, written.attributes);
  const membership = omitKeys(failed.membership, written.membership);
  if (isEmptyRecord(attributes) && isEmptyRecord(membership)) {
    return void 0;
  }
  return withFields(failed, attributes, membership);
};
var withFields = (write, attributes, membership) => {
  const { attributes: _attributes, membership: _membership, ...rest } = write;
  return {
    ...rest,
    ...attributes === void 0 ? {} : { attributes },
    ...membership === void 0 ? {} : { membership }
  };
};
var omitKeys = (source, written) => {
  if (!source || !written) {
    return source;
  }
  return pickKeys(source, (key) => !Object.prototype.hasOwnProperty.call(written, key));
};
var pickKeys = (source, keep) => {
  if (!source) {
    return source;
  }
  const result = {};
  for (const key of Object.keys(source)) {
    if (keep(key)) {
      result[key] = source[key];
    }
  }
  return result;
};
var stampAll = (record, at) => {
  const stamps = {};
  for (const key of Object.keys(record != null ? record : {})) {
    stamps[key] = at;
  }
  return stamps;
};
var keepStampsOf = (stamps, write) => ({
  attributes: pickKeys(
    stamps.attributes,
    (key) => {
      var _a;
      return Object.prototype.hasOwnProperty.call((_a = write.attributes) != null ? _a : {}, key);
    }
  ),
  membership: pickKeys(
    stamps.membership,
    (key) => {
      var _a;
      return Object.prototype.hasOwnProperty.call((_a = write.membership) != null ? _a : {}, key);
    }
  )
});
var isEmptyRecord = (record) => {
  return !record || Object.keys(record).length === 0;
};
var stripNonIdempotentWrites = (attributes) => {
  if (!attributes) {
    return attributes;
  }
  let result;
  for (const codeName of Object.keys(attributes)) {
    const parsed = parseAttributeWrite(attributes[codeName]);
    if (parsed.ok && parsed.write.kind === "add") {
      result = result != null ? result : { ...attributes };
      delete result[codeName];
    }
  }
  return result != null ? result : attributes;
};

// src/replay-registry.ts
var ReplayRegistry = class {
  constructor(options = {}) {
    /** Per target key (see `targetKey`): the failed write awaiting a resend. */
    __publicField(this, "records", /* @__PURE__ */ new Map());
    /** Per target: which send each key was last carried by. */
    __publicField(this, "sentSeqs", /* @__PURE__ */ new Map());
    __publicField(this, "epoch", 0);
    __publicField(this, "seq", 0);
    __publicField(this, "ttlMs");
    __publicField(this, "maxReplays");
    __publicField(this, "now");
    var _a, _b, _c;
    this.ttlMs = (_a = options.ttlMs) != null ? _a : PENDING_WRITE_TTL_MS;
    this.maxReplays = (_b = options.maxReplays) != null ? _b : MAX_WRITE_REPLAYS;
    this.now = (_c = options.now) != null ? _c : Date.now;
  }
  /** Whether any record of this kind awaits a replay. */
  has(kind) {
    for (const key of this.records.keys()) {
      if (kindOf(key) === kind) {
        return true;
      }
    }
    return false;
  }
  /** A write goes out: its keys now belong to this send and leave the record. */
  sent(kind, write) {
    const ticket = this.nextTicket(kind, write);
    this.retire(kind, write);
    return ticket;
  }
  /**
   * A write got no answer. Registered under its own target, in the epoch it
   * was sent in, with the keys no later send has carried. An identity change
   * ends the epoch, so a write of the previous user never registers.
   */
  failed(kind, ticket, write) {
    if (ticket.epoch !== this.epoch) {
      return;
    }
    const owned = keysStillOwnedBy(write, this.sentKeys(kind, write), ticket.seq);
    if (!owned) {
      return;
    }
    const key = targetKey(kind, owned);
    this.records.set(key, recordFailedWrite(this.records.get(key), owned, this.now()));
  }
  /**
   * What to resend for this kind on a connect: the record for the target the
   * session is on now, with its expired keys dropped and the token of now,
   * counted as an attempt before it goes out — so a record replaced while
   * the replay is out still carries the count, which an answer resets. The
   * records for targets the session has left are dropped with their reason;
   * so is a record already resent that many times without an answer.
   */
  startReplay(kind, target, token) {
    const dropped = [];
    const current = targetKey(kind, target);
    for (const key of [...this.records.keys()]) {
      if (kindOf(key) === kind && key !== current) {
        this.records.delete(key);
        dropped.push(
          kind === "user" ? "it belongs to another user" : "the SDK is no longer on that company"
        );
      }
    }
    const recorded = this.records.get(current);
    if (!recorded) {
      return { kind: "nothing", dropped };
    }
    const fresh = withoutExpiredKeys(recorded, this.now(), this.ttlMs);
    if (!fresh) {
      this.records.delete(current);
      return { kind: "refused", reason: "it is older than the replay window", dropped };
    }
    if (fresh.replays >= this.maxReplays) {
      this.records.delete(current);
      return {
        kind: "refused",
        reason: "it was already resent and the server did not answer",
        dropped
      };
    }
    const pending = { ...fresh, replays: fresh.replays + 1 };
    this.records.set(current, pending);
    const write = { ...pending.write, token };
    return { kind: "send", write, ticket: this.nextTicket(kind, write), dropped };
  }
  /**
   * A replay was answered. Retires the keys this send still owns and says
   * whether the session may fold the write into its cache: not after a reset
   * or an identity change, and not for a target the session has left.
   */
  replayAnswered(kind, ticket, write, target) {
    if (ticket.epoch !== this.epoch) {
      return false;
    }
    const landed = keysStillOwnedBy(write, this.sentKeys(kind, write), ticket.seq);
    if (landed) {
      this.retire(kind, landed);
    }
    const key = targetKey(kind, write);
    const remaining = this.records.get(key);
    if (remaining) {
      this.records.set(key, { ...remaining, replays: 0 });
    }
    return isSameTarget(kind, write, target);
  }
  /** The session ended: nothing from before it is resent or folded in. */
  reset() {
    this.records.clear();
    this.sentSeqs.clear();
    this.epoch += 1;
  }
  nextTicket(kind, write) {
    this.seq += 1;
    const key = targetKey(kind, write);
    this.sentSeqs.set(key, noteSentKeys(this.sentSeqs.get(key), write, this.seq));
    return { seq: this.seq, epoch: this.epoch };
  }
  sentKeys(kind, write) {
    var _a;
    return (_a = this.sentSeqs.get(targetKey(kind, write))) != null ? _a : { attributes: {}, membership: {} };
  }
  retire(kind, write) {
    const key = targetKey(kind, write);
    const record = this.records.get(key);
    if (!record) {
      return;
    }
    const remaining = retireWrittenKeys(record, write);
    if (remaining) {
      this.records.set(key, remaining);
    } else {
      this.records.delete(key);
    }
  }
};
var SEPARATOR = "\0";
var targetKey = (kind, write) => {
  var _a, _b;
  const company = kind === "company" ? (_a = write.externalCompanyId) != null ? _a : "" : "";
  return `${kind}${SEPARATOR}${(_b = write.externalUserId) != null ? _b : ""}${SEPARATOR}${company}`;
};
var kindOf = (key) => key.slice(0, key.indexOf(SEPARATOR));
var isSameTarget = (kind, write, target) => {
  if (write.externalUserId !== target.externalUserId) {
    return false;
  }
  return kind === "user" || write.externalCompanyId === target.externalCompanyId;
};

// src/debug-flag.ts
var DEBUG_QUERY_PARAM = "usertour_debug";
var splitTokens = (value) => value.split(",").map((token) => token.trim()).filter(Boolean);
var tokenNamesNamespace = (token, namespace) => {
  if (token === "*" || token === namespace || token.startsWith(`${namespace}:`)) {
    return true;
  }
  if (token.endsWith("*")) {
    const prefix = token.slice(0, -1);
    return namespace.startsWith(prefix) || `${namespace}:`.startsWith(prefix);
  }
  return false;
};
var debugFlagNamesNamespace = (storageValue, namespace) => {
  return splitTokens(storageValue).some((token) => tokenNamesNamespace(token, namespace));
};
var debugQueryParamEnabled = (search) => {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  for (const pair of normalized.split("&")) {
    const [key, value = ""] = pair.split("=");
    if (key !== DEBUG_QUERY_PARAM) {
      continue;
    }
    const decoded = decodeURIComponent(value).toLowerCase();
    return decoded === "" || decoded === "1" || decoded === "true";
  }
  return false;
};
var withDebugNamespace = (storageValue, namespace) => {
  const tokens = splitTokens(storageValue);
  const own = `${namespace}:*`;
  return (tokens.includes(own) ? tokens : [...tokens, own]).join(",");
};
var withoutDebugNamespace = (storageValue, namespace) => {
  return splitTokens(storageValue).filter((token) => token === "*" || !tokenNamesNamespace(token, namespace)).join(",");
};
export {
  ATTRIBUTE_DELETE,
  AUTO_START_CAPABILITIES,
  AbortController,
  ArrayProto,
  CLIENT_DISCONNECT_REASON,
  CONTENT_ACTION_CAPABILITIES,
  CONTENT_REFERENCE_TARGET_TYPES,
  CONTENT_TYPE_TRAITS,
  DATE_PICKER_OPERATORS,
  DEBUG_QUERY_PARAM,
  DEFAULT_FREQUENCY,
  ENV_TARGETED_CAPABILITIES,
  LOCALIZED_UNITS_SCHEMA_VERSION,
  MAX_WRITE_REPLAYS,
  OPERATORS_BY_DATATYPE,
  PENDING_WRITE_TTL_MS,
  RECONNECT_BASE_DELAY_MS,
  RECONNECT_JITTER,
  RECONNECT_MAX_DELAY_MS,
  ReplayRegistry,
  SERVER_DISCONNECT_REASON,
  SERVER_EVALUATED_CONDITION_TYPES,
  STEP_CAPABILITIES,
  TRANSLATION_UNIT_FIELDS,
  VALUELESS_OPERATORS,
  XMLHttpRequest,
  absoluteUrl,
  allConditionsHaveIds,
  applyAttributeWrite,
  applyContentsTranslationUnits,
  applyVersionDataTranslationUnits,
  assignConditionIds,
  assignableWindow,
  attributeCacheChanged,
  attributeDataEqual,
  blankLocalizedUnitClones,
  bucketPoint,
  bucketValue,
  buildConfig,
  buildGoogleFontUrl,
  buildLocalizedFlowBackup,
  buildLocalizedFlowSavePayload,
  buildLocalizedVersionDataBackup,
  buildLocalizedVersionDataSavePayload,
  capitalizeFirstLetter,
  coerceAttributeValue,
  collectActionListDestinations,
  collectContentsDestinations,
  collectOutdatedUnitPaths,
  collectOutdatedVersionDataPaths,
  collectVersionDataDestinations,
  conditionsIsSame,
  contentListDestination,
  convertSettings,
  convertTimeConditionLegacyToV2,
  convertToCssVars,
  countMissingTranslations,
  countMissingVersionDataTranslations,
  createDefaultResourceCenterData,
  createLocalizedWorkingContents,
  createLocalizedWorkingVersionData,
  cuid,
  debugFlagNamesNamespace,
  debugQueryParamEnabled,
  deepClone,
  deepMergeThemeSettings,
  defaultColumn,
  defaultFrequencyFor,
  defaultStep,
  deriveThemeAutoColors,
  document,
  duplicateChecklistData,
  duplicateConfig,
  duplicateData,
  duplicateLauncherData,
  duplicateStep,
  duplicateTarget,
  duplicateTriggers,
  effectiveDataType,
  environmentSelectionMissing,
  evaluateAttributeCondition,
  evaluateRule,
  evaluateRulesConditions,
  evaluateTimeCondition,
  evaluateUrlCondition,
  executeUserCode,
  extractContentsTranslationUnits,
  extractLinkUrl,
  extractQuestionData,
  extractTranslatableUnits,
  extractVersionDataTranslationUnits,
  fetch,
  filterConditionsByType,
  filterNullAttributes,
  firstLetterToUpperCase,
  formatDate,
  formatElementPath,
  generateDefaultStepName,
  generateStateColors,
  generateUniqueCopyName,
  getAttributeType,
  getAuthToken,
  getAutoStartCapabilities,
  getErrorMessage,
  getRandomColor,
  hasError,
  hasMissingRequiredData,
  hexToHSLAString,
  hexToHSLString,
  hexToRGBStr,
  hexToRgb2 as hexToRgb,
  humanize,
  inferWriteDataType,
  isArray,
  isAttributeOperation,
  isBoolean,
  isBucketingDataType,
  isClickableElement,
  isConditionsActived,
  isDate,
  isDisplayOnlyBlockType,
  isDocument,
  isEmptyObject,
  isEmptyString,
  isEqual,
  isFile,
  isFormData,
  isFunction,
  isHttpUrl,
  isMatchUrlPattern,
  isMissingRequiredData,
  isNearWhite,
  isNull,
  isNullish,
  isNumber,
  isObject,
  isPublishedAtLeastOneEnvironment,
  isPublishedInAllEnvironments,
  isQuestionElement,
  isRestrictedType,
  isRetryableHandshakeError,
  isRichTextEmpty,
  isSafeDestinationUrl,
  isString,
  isTimeConditionDataLegacy,
  isTimeConditionDataV2,
  isTranslatableText,
  isTranslationUnitOptional,
  isUint8Array,
  isUndefined,
  isUrl,
  isValidISO8601,
  isValidRandomMax,
  isVersionDataLocalizable,
  isWithinLimit,
  keysStillOwnedBy,
  linkDestination,
  location,
  matchTranslationByLocale,
  mergeAttributeCache,
  mergeFailedWrite,
  mergeLocalizedEditorContents,
  mergeLocalizedVersionData,
  mergeThemeDefaultSettings,
  missingBucketValues,
  nativeForEach,
  nativeIndexOf,
  navigateDestination,
  navigator,
  needsDarkText,
  normalizeIsoDateTime,
  normalizeLegacyAttributeWrites,
  normalizeTimeConditionData,
  noteSentKeys,
  operatorsFor,
  parseAttributeWrite,
  parseOverridePlan,
  parseUrlParams,
  processQuestionElements,
  readDestination,
  reconnectDelayMs,
  recordFailedWrite,
  reduceConnection,
  regenerateConditionIds,
  remapContentsTranslationIdentifiers,
  remapFlowTranslationIdentifiers,
  remapVersionDataTranslationIdentifiers,
  removeAuthToken,
  replaceUserAttr,
  requiresEnvironmentScope,
  resolvePlanFeatures,
  resolveUserLocaleCode,
  retireWrittenKeys,
  serializeBlockName,
  serializeLinkTemplate,
  setAuthToken,
  shouldLoadGoogleFont,
  splitOperatorTemplate,
  storage,
  stripNonIdempotentWrites,
  toNumericValue,
  userAgent,
  uuidV4,
  validateConditionByType,
  validateContent,
  validateCurrentPage,
  validateElement,
  validateEvent,
  validateEventAttr,
  validateSegment,
  validateTextFill,
  validateTextInput,
  validateTime,
  validateUserAttr,
  wait,
  win as window,
  withDebugNamespace,
  withoutDebugNamespace,
  withoutExpiredKeys,
  withoutWrittenKeys,
  writeDestination
};
